/**
 * ROOF ROUND E2 — DERIVED FRAMING + EXPRESSIVE RAFTER TAILS (docs/MODELER.md).
 * The skeleton API's parked consumer, activated: representation-grade framing derived
 * entirely from the mass + the roof TYPE's firm standards. Explicitly NOT a
 * structural tool — no blocking, collar ties, hangers, or engineering, ever.
 *
 * NOTHING here is an element. Members derive at generation time exactly like tags
 * derive from openings: reshape the roof and it re-frames. The "instancing economics"
 * are generation-side — ONE cross-section definition per member size, stamped per
 * placement into one merged meshOnly solid per roof (the viewport's per-group merged
 * meshes ARE the render economics; member counts are small). 3D + section come free
 * through the mesh channel (sections cut mesh triangles); RCP/plan representation is
 * deferred per the round spec.
 *
 * FIELD RAFTERS (BODY portions gated by the instance `showFraming` study toggle):
 * per face, strips run along the face's `downhill`, spaced along the plate line
 * (centered array at the TYPE's rafterSpacingIn), TRIMMED to the face polygon by
 * Clipper — hip/valley jacks shorten automatically. Vertically the members occupy
 * the assembly's structural core zone (bottom seated at the core's underside, depth
 * from the TYPE's rafterSize, all plumb-converted by the face's √(1+m²)). The E1
 * eave crop applies to field rafters: past the bearing line their bottom rises so
 * the remaining depth is the edge's eaveStructureDepthIn. On a TAILS edge (FRAMING
 * SESSION Phase 2, refined in the 2026-08-13 fix round): field rafters get a
 * BIRDSMOUTH at the plate — seat flat at the bearing height (the level plate line
 * from the wall side's plate rule) — ending with a PLUMB CUT at the outer face of
 * the host wall's FRAMING (core) layer, never past it into the sheathing; the heel
 * and the end cut are one plumb face, derived from the HOST WALL's assembly
 * (hostWallOffsetsFor; no resolvable host = die at the wall line, never invented
 * dimensions). Piece boundaries inside one member weld (stripSeamEdges — no line
 * where the birdsmouth bands or a tail's back-span/profiled joint meet). The field
 * module also YIELDS to the decorative tails (shiftRaftersClearOfTails — tails own
 * their positions; unclearable rafters are omitted with a console.warn, never
 * silently). RIDGE/HIP/VALLEY members run along the classified skeleton edges
 * (same nominal size v1), tops aligned with the rafter tops at the edge.
 *
 * OPEN EAVES (the eave-styling round, 2026-08-10 — instance `eaveMode: 'open'`):
 * the eave-zone mass thins to decking + finishes (underside.ts), and the eave-zone
 * STRUCTURE renders here ALWAYS — exposed field-rafter and hip/valley ends, no study
 * toggle needed — so the open eave actually shows its rafters in 3D. Body portions
 * stay inside the mass, still gated by showFraming; tails faces keep their tails as
 * the show members (field rafters die at the wall line there either way).
 *
 * DECORATIVE TAILS (instance `tailsDefault` for every eave edge, per-edge
 * `node.edge.tails` override; independent of showFraming — they are show
 * architecture, not a framing study): the framing of the EAVES, independent of and
 * not necessarily aligned with the roof's own field rafters. Larger stock
 * (tailSize) laid to a MAX spacing, EQUALLY distributed along each eave edge with
 * a member at each end (tailStations — Matt, 2026-08-11), reaching the eave depth
 * OUT and 2× the eave depth BACK IN (derived from that edge's own overhang, never
 * typed). The bottom of the exposed run is shaped by the tailStyle profile (kernel
 * roofFraming.ts — the curated family). Tails hang below the decking (the solid's
 * eave zone thins to decking + finishes when tails are on — see generate3d), and
 * field rafters on a tails face die at the wall line behind the decking, the
 * standard construction. A tail whose tip would leave its face's plan region (hip
 * corners) is skipped — corner tails are the recorded refinement.
 *
 * RAKE OUTLOOKERS + BARGE RAFTERS (ROOF ROUND E4, 2026-08-11; r2 same day from
 * Matt's screenshots + reference photo — the tails pattern applied to RAKES):
 * per-gable-edge `barge` toggle (instance `bargeDefault` for all rakes) emits ONE
 * barge rafter per rake — OUTER face aligned with the roof edge (the rake
 * sheathing line, i.e. the overhang line; it hangs INBOARD by its width, never
 * past the intended overhang), top at the decking underside, lower end cut with
 * the roof's own tailStyle over THE PROFILE RUN RULE's intrinsic run (2× member
 * depth, shared with the tails so barge and tails read as one profile family in
 * elevation), upper end a plumb cut at the ridge station where the partner barge
 * from the other face lands on the SAME line (both rakes of a gable are collinear
 * on its overhang line by construction) — the two plumb faces meet watertight,
 * the classic peak joint — and DECORATIVE OUTLOOKER BRACKETS below it: heavy
 * stock (curated OUTLOOKER_SIZES), level, projecting from the rake wall out to
 * the barge's outer face, tops seated on the barge's underside, outer ends shaped
 * by the end style (Square + the tail-profile family), laid along the rake to a
 * MAX spacing (tailStations, plan-measured, clear of the barge's shaped tip).
 * (r1's flat hidden-at-decking outlookers with a 2× reach-in are superseded —
 * Matt wants the expressive read, not modern concealed practice.) Show members
 * like tails: independent of showFraming, feature edges on.
 */
import earcutFn from 'earcut'
import type { Path2D, Pt2, ResolvedElement } from '@aj/bim-kernel'
import { tintMaterialId } from '@aj/bim-kernel'
import { FASCIA_SIZES, FRIEZE_SIZES, OUTLOOKER_SIZES, SOFFIT_FRAMING_SIZES, TAIL_SIZES, lumberSize, roofRafterMemberSize, tailProfile } from '@aj/bim-kernel'
import type { ElementSolid } from '../wall/generate3d'
import { clipperReady, differenceClosed, intersectClosed, signedArea } from '../../clipper'
import { newMeshAcc, pushTri, meshAccUvs, type MeshAcc } from './meshAcc'
import type { ResolvedRoofSolve, RoofEdgeSpec, RoofFace } from './solve'
import { roofSkeletonFor } from './skeleton'
import { flatSoffitZ } from './soffit'
import { roofCutRings } from './verticalCuts'
import type { RoofJoinCtx } from './join'
import { planeOf, type RoofEnvelopePiece } from './envelope'

const AREA_EPS = 1e-6

/** E4 r3 (Matt): outlooker brackets run this far PAST the barge's outer face, so
 *  their shaped ends show beyond it — a firm convention, not a parameter. */
const OUTLOOKER_PAST_BARGE_FT = 4 / 12

/** FRAMING SESSION Phase 2: clear gap a field rafter keeps to a decorative tail
 *  beyond the touching distance (member half-widths). */
const TAIL_CLEAR_GAP_FT = 1.5 / 12

/**
 * FRAMING SESSION fix round (Matt, 2026-08-13): a member emitted as several
 * prism/profiled PIECES (the birdsmouth bands of one rafter, a tail's back
 * span + its profiled exposed run) must READ as one uniform member — the
 * boundary where the pieces meet draws no line. Drops edge segments whose BOTH
 * endpoints lie on the vertical plane { p·d = station } (the internal weld
 * plane); real outlines crossing the plane keep drawing (only one endpoint
 * lands on it).
 */
function stripSeamEdges(edges: number[], d: Pt2, station: number, tol = 1e-4): number[] {
  const out: number[] = []
  for (let i = 0; i + 5 < edges.length; i += 6) {
    const a = Math.abs(edges[i] * d[0] + edges[i + 1] * d[1] - station)
    const b = Math.abs(edges[i + 3] * d[0] + edges[i + 4] * d[1] - station)
    if (a <= tol && b <= tol) continue
    out.push(edges[i], edges[i + 1], edges[i + 2], edges[i + 3], edges[i + 4], edges[i + 5])
  }
  return out
}

/** The assembly build-up split about its structural core (layers ordered top→bottom). */
export function assemblyZonesFt(el: ResolvedElement): { aboveFt: number; coreFt: number; belowFt: number } {
  const a = el.assembly
  if (!a || !a.layers.length) return { aboveFt: 0, coreFt: 0, belowFt: 0 }
  const nL = a.layers.length
  const s = Math.min(Math.max(a.coreBoundary?.start ?? 0, 0), nL - 1)
  const e = Math.min(Math.max(a.coreBoundary?.end ?? nL - 1, s), nL - 1)
  let above = 0, core = 0, below = 0
  for (let i = 0; i < nL; i++) {
    const th = a.layers[i].thicknessFt || 0
    if (i < s) above += th
    else if (i <= e) core += th
    else below += th
  }
  return { aboveFt: above, coreFt: core, belowFt: below }
}

/** The eave decking thickness in feet (instance eave styling since the eave-styling
 *  round), clamped inside the structure zone. */
export function deckingFt(el: ResolvedElement): number {
  const zones = assemblyZonesFt(el)
  const raw = Number(el.params.deckingThicknessIn)
  const ft = Number.isFinite(raw) && raw > 0 ? raw / 12 : 0.75 / 12
  return Math.max(1 / 48, Math.min(ft, Math.max(zones.coreFt + zones.belowFt - 1 / 48, 1 / 48)))
}

/** The framing/tail member material — the assembly's first core layer. */
export function framingMaterialId(el: ResolvedElement): string {
  const a = el.assembly
  if (!a || !a.layers.length) return 'default'
  const s = Math.min(Math.max(a.coreBoundary?.start ?? 0, 0), a.layers.length - 1)
  return a.layers[s].materialId ?? 'default'
}

export function pointInRing(pt: Pt2, ring: Pt2[]): boolean {
  let inside = false
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i], [xj, yj] = ring[j]
    if ((yi > pt[1]) !== (yj > pt[1]) && pt[0] < ((xj - xi) * (pt[1] - yi)) / (yj - yi) + xi) {
      inside = !inside
    }
  }
  return inside
}

const keepRings = (rings: Pt2[][]) => rings.filter((r) => r.length >= 3 && signedArea(r) > AREA_EPS)

/** plan distance from a point to the infinite line through `o` along unit `d`
 *  (shared with trim.ts — the skeleton-edge → boundary-spec matcher). */
export function distToLine(p: Pt2, o: Pt2, d: Pt2): number {
  return Math.abs(d[0] * (p[1] - o[1]) - d[1] * (p[0] - o[0]))
}

export interface SpecLine { o: Pt2; d: Pt2; n: Pt2; spec: RoofEdgeSpec; i: number }

/**
 * Each boundary spec's ROOF-EDGE line: the bearing edge offset OUTWARD by its
 * overhang, with the outward unit normal. Moved here from trim.ts in E4 (framing
 * needs it too for the rake → gable-spec mapping, and trim already imports from
 * this module — the other direction would cycle). A skeleton perimeter edge maps
 * back to its spec GEOMETRICALLY — both endpoints on the spec's line — the only
 * mapping that works for RAKES, whose gable edge solves no face, and that is
 * immune to the solver's winding normalization.
 */
export function specLines(solved: ResolvedRoofSolve): SpecLine[] {
  const out: SpecLine[] = []
  for (let i = 0; i < solved.specs.length; i++) {
    const spec = solved.specs[i]
    const dx = spec.b[0] - spec.a[0], dy = spec.b[1] - spec.a[1]
    const len = Math.hypot(dx, dy)
    if (len < 1e-9) continue
    const d: Pt2 = [dx / len, dy / len]
    let n: Pt2 = [-d[1], d[0]]
    const mid: Pt2 = [(spec.a[0] + spec.b[0]) / 2, (spec.a[1] + spec.b[1]) / 2]
    if (pointInRing([mid[0] + n[0] * 0.01, mid[1] + n[1] * 0.01], solved.ring)) n = [-n[0], -n[1]]
    out.push({ o: [spec.a[0] + n[0] * spec.overhangFt, spec.a[1] + n[1] * spec.overhangFt], d, n, spec, i })
  }
  return out
}

/** E5: is a plan point covered by any join-target footprint? */
export function coveredByRings(p: Pt2, rings: Pt2[][]): boolean {
  for (const r of rings) if (pointInRing(p, r)) return true
  return false
}

/**
 * E5: the sub-intervals of segment A→B (params 0..1) NOT covered by any of the
 * rings — the fascia/gutter clipping primitive: crossing parameters split the
 * run, each piece keeps or drops by a midpoint test, contiguous keeps merge.
 * No rings = the whole segment, so the no-pick path costs nothing.
 */
export function uncoveredIntervals(A: Pt2, B: Pt2, rings: Pt2[][]): [number, number][] {
  if (!rings.length) return [[0, 1]]
  const cuts: number[] = [0, 1]
  const d1: Pt2 = [B[0] - A[0], B[1] - A[1]]
  for (const r of rings) {
    for (let i = 0; i < r.length; i++) {
      const P = r[i], Q = r[(i + 1) % r.length]
      const d2: Pt2 = [Q[0] - P[0], Q[1] - P[1]]
      const den = d1[0] * d2[1] - d1[1] * d2[0]
      if (Math.abs(den) < 1e-12) continue
      const t = ((P[0] - A[0]) * d2[1] - (P[1] - A[1]) * d2[0]) / den
      const s = ((P[0] - A[0]) * d1[1] - (P[1] - A[1]) * d1[0]) / den
      if (t > 1e-9 && t < 1 - 1e-9 && s >= -1e-9 && s <= 1 + 1e-9) cuts.push(t)
    }
  }
  cuts.sort((x, y) => x - y)
  const out: [number, number][] = []
  for (let i = 0; i + 1 < cuts.length; i++) {
    const t0 = cuts[i], t1 = cuts[i + 1]
    if (t1 - t0 < 1e-6) continue
    const mid: Pt2 = [A[0] + d1[0] * ((t0 + t1) / 2), A[1] + d1[1] * ((t0 + t1) / 2)]
    if (coveredByRings(mid, rings)) continue
    const last = out[out.length - 1]
    if (last && Math.abs(last[1] - t0) < 1e-9) last[1] = t1
    else out.push([t0, t1])
  }
  return out
}

/** a partner top-surface face for SEAM suppression (E5 r4): where a joined
 *  roof's top feature edge — or a rake member run (r5) — has the partner's
 *  sheet continuing COPLANAR on its far side, the stretch is suppressed; the
 *  pair reads as one surface. Valley edges survive automatically (z diverges
 *  immediately past them). Lives here with the other shared low-level helpers
 *  (join.ts imports framing values, so it can't live there without a cycle). */
export interface SeamFace {
  ring: Pt2[]
  zTopAt: (p: Pt2) => number
}

/**
 * The seam-coverage primitive (E5 r4, generalized in r5 for rake members): the
 * KEPT sub-intervals (params 0..1) of the probe segment P2→Q2 — the caller has
 * already offset it to the partner's side — after removing every stretch a
 * partner sheet suppresses. Two modes, one z test at the covered stretch's
 * midpoint against the caller's own top:
 * - 'coplanar' (feature edges): suppress only where the partner rides at the
 *   SAME level (±2.5e-3 ft) — the invisible butt seam; a step edge where the
 *   partner sits below still draws, and valleys survive.
 * - 'cover' (barge/bracket members): suppress wherever the partner rides AT OR
 *   ABOVE (≥ −2.5e-3 ft) — a member under another roof's body is buried whether
 *   the sheets touch or not; only a partner BELOW leaves the stretch exposed.
 */
export function seamKeptIntervals(
  P2: Pt2, Q2: Pt2,
  zTopOwnAt: (p: Pt2) => number,
  seamFaces: SeamFace[],
  mode: 'coplanar' | 'cover' = 'coplanar',
): [number, number][] {
  let kept: [number, number][] = [[0, 1]]
  if (!seamFaces.length) return kept
  for (const s of seamFaces) {
    // covered intervals of the probe segment = complement of the uncovered
    const unc = uncoveredIntervals(P2, Q2, [s.ring])
    const covered: [number, number][] = []
    let cur = 0
    for (const [a, b] of unc) {
      if (a - cur > 1e-6) covered.push([cur, a])
      cur = b
    }
    if (1 - cur > 1e-6) covered.push([cur, 1])
    for (const [a, b] of covered) {
      const tm = (a + b) / 2
      const pm: Pt2 = [P2[0] + (Q2[0] - P2[0]) * tm, P2[1] + (Q2[1] - P2[1]) * tm]
      const dz = s.zTopAt(pm) - zTopOwnAt(pm)
      const suppress = mode === 'coplanar' ? Math.abs(dz) <= 2.5e-3 : dz >= -2.5e-3
      if (!suppress) continue
      const next: [number, number][] = []
      for (const [k0, k1] of kept) {
        if (b <= k0 + 1e-9 || a >= k1 - 1e-9) { next.push([k0, k1]); continue }
        if (a > k0 + 1e-6) next.push([k0, a])
        if (b < k1 - 1e-6) next.push([b, k1])
      }
      kept = next
      if (!kept.length) return kept
    }
  }
  return kept
}

/**
 * MEMBER-FRAME PROFILE points (E4 r4 — Matt: "the profiles aren't treated
 * relative to the angle of the rafter, instead their cuts are all straight
 * vertical"): the shaped END of a member in (a, z) section space, with the cut's
 * run measured ALONG THE MEMBER AXIS and its depth PERPENDICULAR to it — the
 * profile rides the rafter like a shop template instead of shearing onto a
 * vertical grid, and the closing end face lands SQUARE to the axis (the caller's
 * next outline point is the tip-top corner). (a, z) is a true metric plane (both
 * feet), so the rotation is exact.
 *
 * `aTip`/`zTip` = the tip END's top corner; `s` = the top line's dz/da;
 * `sigma` = +1 when the tip lies toward increasing a (tails, brackets), −1 when
 * it lies toward decreasing a (the barge's eave end); `D` = the member's TRUE
 * (perpendicular) depth; `LpAxis` = the cut's axis-measured run. Returns points
 * in profile order (u ascending, cut start → tip); each [u, frac] maps to
 * TipTop − t̂·(1−u)·Lp + η̂·frac·D with t̂ the axis unit toward the tip and η̂
 * the down-perpendicular.
 */
export function memberEndProfile(
  profilePts: readonly [number, number][],
  aTip: number, zTip: number, s: number, sigma: number, D: number, LpAxis: number,
): [number, number][] {
  const rt = Math.sqrt(1 + s * s)
  const out: [number, number][] = []
  for (const [u, fracD] of profilePts) {
    const back = (1 - u) * LpAxis
    out.push([
      aTip - (sigma * back) / rt + (fracD * D * s) / rt,
      zTip - (sigma * back * s) / rt - (fracD * D) / rt,
    ])
  }
  return out
}

/**
 * Emit one PROFILED prismatic member (E4 extraction of the tails' inline emitter,
 * now shared by tails, outlooker exposed runs, and barge rafters): `outline` is the
 * member's CCW section in (a, z) — a measured along `planAt`'s axis (plan direction
 * `d`), z absolute — swept `halfW` to each side along `e`. Side caps via earcut,
 * lateral strips around the outline, and the outline's show-member FEATURE EDGES on
 * both side faces (the elevation read).
 */
export function emitProfiledMember(
  acc: MeshAcc,
  edges: number[],
  planAt: (a: number) => Pt2,
  d: Pt2,
  e: Pt2,
  halfW: number,
  outline: [number, number][],
): void {
  const to3 = (a: number, z: number, side: number): number[] =>
    [planAt(a)[0] + e[0] * side, planAt(a)[1] + e[1] * side, z]
  const e3 = [e[0], e[1], 0]

  // side caps via earcut in (a,z); orient each side's normal to ±e
  const flat = outline.flatMap((p) => [p[0], p[1]])
  const tris = earcutFn(flat)
  for (const side of [halfW, -halfW]) {
    for (let i = 0; i < tris.length; i += 3) {
      const A = to3(outline[tris[i]][0], outline[tris[i]][1], side)
      const B = to3(outline[tris[i + 1]][0], outline[tris[i + 1]][1], side)
      const C = to3(outline[tris[i + 2]][0], outline[tris[i + 2]][1], side)
      const nx2 = (B[1] - A[1]) * (C[2] - A[2]) - (B[2] - A[2]) * (C[1] - A[1])
      const ny2 = (B[2] - A[2]) * (C[0] - A[0]) - (B[0] - A[0]) * (C[2] - A[2])
      const dot = nx2 * e3[0] + ny2 * e3[1]
      const want = side > 0
      if ((dot > 0) === want) pushTri(acc, A, B, C)
      else pushTri(acc, A, C, B)
    }
  }
  // lateral strips around the outline; outward = right of CCW travel in (a,z)
  for (let i = 0; i < outline.length; i++) {
    const j = (i + 1) % outline.length
    const [ai, zi] = outline[i], [aj, zj] = outline[j]
    if (Math.abs(ai - aj) < 1e-9 && Math.abs(zi - zj) < 1e-9) continue
    const da = aj - ai, dz = zj - zi
    const oLen = Math.hypot(da, dz) || 1
    const out3 = [d[0] * (dz / oLen), d[1] * (dz / oLen), -da / oLen]
    const Pp = to3(ai, zi, halfW), Pm = to3(ai, zi, -halfW)
    const Qp = to3(aj, zj, halfW), Qm = to3(aj, zj, -halfW)
    const cn = [
      (Pm[1] - Pp[1]) * (Qm[2] - Pp[2]) - (Pm[2] - Pp[2]) * (Qm[1] - Pp[1]),
      (Pm[2] - Pp[2]) * (Qm[0] - Pp[0]) - (Pm[0] - Pp[0]) * (Qm[2] - Pp[2]),
      (Pm[0] - Pp[0]) * (Qm[1] - Pp[1]) - (Pm[1] - Pp[1]) * (Qm[0] - Pp[0]),
    ]
    if (cn[0] * out3[0] + cn[1] * out3[1] + cn[2] * out3[2] > 0) {
      pushTri(acc, Pp, Pm, Qm); pushTri(acc, Pp, Qm, Qp)
    } else {
      pushTri(acc, Pp, Qm, Pm); pushTri(acc, Pp, Qp, Qm)
    }
    // show-member feature edges: the outline on both side faces
    edges.push(Pp[0], Pp[1], Pp[2], Qp[0], Qp[1], Qp[2])
    edges.push(Pm[0], Pm[1], Pm[2], Qm[0], Qm[1], Qm[2])
  }
}

/** GRAIN-ALIGNED FRAMING round: derive a member's own local UV axis straight from
 *  its own ring + z-surfaces — no caller plumbing needed, so every prism-built
 *  member (studs, plates, joists, rafters, ridge/hip/valley, trim boards) gets it
 *  automatically regardless of tilt/rotation. `dir` runs along the ring's own
 *  LONGEST edge (real lumber is long-and-thin, so this is always the member's true
 *  length axis, slope-projected via the TOP surface — correct for a level member
 *  too, sq=1 falls out on its own), `perp` is the horizontal cross-axis
 *  (cross(worldUp, dir)), falling back to world X only for a purely vertical
 *  member (studs), where world projection already gets grain direction right.
 *  World feet throughout, matching computeWorldUvs' convention so texture.repeat
 *  behaves identically on framing vs. every other textured surface. */
function memberUvOf(ring: Pt2[], top: number[][]): (p: number[]) => [number, number] {
  let bestLen = -1, bi = 0
  for (let i = 0; i < ring.length; i++) {
    const j = (i + 1) % ring.length
    const dx = ring[j][0] - ring[i][0], dy = ring[j][1] - ring[i][1]
    const len = Math.hypot(dx, dy)
    if (len > bestLen) { bestLen = len; bi = i }
  }
  const bj = (bi + 1) % ring.length
  const p0 = top[bi], p1 = top[bj]
  const dlen = Math.hypot(p1[0] - p0[0], p1[1] - p0[1], p1[2] - p0[2]) || 1
  const dx = (p1[0] - p0[0]) / dlen, dy = (p1[1] - p0[1]) / dlen, dz = (p1[2] - p0[2]) / dlen
  let px = -dy, py = dx, pz = 0
  const plen = Math.hypot(px, py, pz)
  if (plen < 1e-6) { px = 1; py = 0; pz = 0 } else { px /= plen; py /= plen; pz /= plen }
  const [ox, oy, oz] = p0
  return (p: number[]) => {
    const x = p[0] - ox, y = p[1] - oy, z = p[2] - oz
    return [x * px + y * py + z * pz, x * dx + y * dy + z * dz]
  }
}

/** Sloped prism over a CCW plan ring between two per-point z surfaces (shared with
 *  trim.ts — fascia boards are the same honest prisms the framing members are).
 *  `edges` (optional): pushes this member's own top/bottom/vertical outline — the
 *  wall-framing round's "crisp edges, not shading alone" treatment, shared here so
 *  every prism-built member (field rafters, ridge/hip/valley, the tail back span)
 *  reads outlined even when it's buried inside the roof mass (framing-only view). */
export function prism(
  m: MeshAcc, ring: Pt2[], zBotOf: (p: Pt2) => number, zTopOf: (p: Pt2) => number, edges?: number[],
): void {
  const flat = ring.flatMap((p) => [p[0], p[1]])
  const tris = earcutFn(flat)
  const bot = ring.map((p) => [p[0], p[1], zBotOf(p)])
  const top = ring.map((p) => [p[0], p[1], zTopOf(p)])
  const uvOf = memberUvOf(ring, top)
  for (let i = 0; i < tris.length; i += 3) {
    const [a, b, c] = [tris[i], tris[i + 1], tris[i + 2]]
    const upOk = (ring[b][0] - ring[a][0]) * (ring[c][1] - ring[a][1])
      - (ring[b][1] - ring[a][1]) * (ring[c][0] - ring[a][0]) > 0
    if (upOk) {
      pushTri(m, top[a], top[b], top[c], uvOf)
      pushTri(m, bot[a], bot[c], bot[b], uvOf)
    } else {
      pushTri(m, top[a], top[c], top[b], uvOf)
      pushTri(m, bot[a], bot[b], bot[c], uvOf)
    }
  }
  // side quads, outward = right of CCW travel (the roof band convention)
  for (let i = 0; i < ring.length; i++) {
    const j = (i + 1) % ring.length
    pushTri(m, top[i], bot[i], bot[j], uvOf)
    pushTri(m, top[i], bot[j], top[j], uvOf)
  }
  if (edges) {
    for (let i = 0; i < ring.length; i++) {
      const P = ring[i], Q = ring[(i + 1) % ring.length]
      edges.push(P[0], P[1], zBotOf(P), Q[0], Q[1], zBotOf(Q))
      edges.push(P[0], P[1], zTopOf(P), Q[0], Q[1], zTopOf(Q))
      edges.push(P[0], P[1], zBotOf(P), P[0], P[1], zTopOf(P))
    }
  }
}

interface FaceFrame {
  d: Pt2      // downhill (member run direction)
  e: Pt2      // along-plate direction (perpendicular to d)
  sq: number  // plumb factor √(1+m²)
}

function frameOf(f: RoofFace): FaceFrame | null {
  const u = f.uphill
  const len = Math.hypot(u[0], u[1])
  if (len < 1e-9) return null
  const d: Pt2 = [-u[0] / len, -u[1] / len]
  return { d, e: [-d[1], d[0]], sq: Math.sqrt(1 + f.m * f.m) }
}

/** Centered layout stations across [lo, hi] at `spacing` (≥1 member) — the FIELD
 *  rafter module (laid at the exact o.c. spacing, leftover split at the ends).
 *  Exported: the floor-framing generator (categories/floor/framing.ts) reuses the
 *  same centered-module layout for joists. */
export function stations(lo: number, hi: number, spacing: number): number[] {
  const span = hi - lo
  if (!(span > 0) || !(spacing > 0)) return []
  const n = Math.max(1, Math.floor(span / spacing) + 1)
  const first = lo + (span - (n - 1) * spacing) / 2
  const out: number[] = []
  for (let k = 0; k < n; k++) out.push(first + k * spacing)
  return out
}

/**
 * A ring's extent along the `e` axis where it meets the bearing line (the line
 * through `origin` perpendicular to `uphill`): on-line vertices PLUS edge
 * crossings — the crossings are what make this work for envelope-fallback faces
 * (sheds), whose rings never touch the line with a vertex. Null = the ring never
 * reaches the line.
 */
export function spanOnBearingLine(ring: Pt2[], origin: Pt2, uphill: Pt2, e: Pt2): [number, number] | null {
  const TOL = 1e-4
  let lo = Infinity, hi = -Infinity
  const dOf = (p: Pt2) => (p[0] - origin[0]) * uphill[0] + (p[1] - origin[1]) * uphill[1]
  const eOf = (p: Pt2) => p[0] * e[0] + p[1] * e[1]
  const n = ring.length
  for (let i = 0; i < n; i++) {
    const P = ring[i], Q = ring[(i + 1) % n]
    const da = dOf(P), db = dOf(Q)
    if (Math.abs(da) <= TOL) {
      const pe = eOf(P)
      lo = Math.min(lo, pe); hi = Math.max(hi, pe)
    }
    if ((da > TOL && db < -TOL) || (da < -TOL && db > TOL)) {
      const t = da / (da - db)
      const pe = eOf([P[0] + (Q[0] - P[0]) * t, P[1] + (Q[1] - P[1]) * t])
      lo = Math.min(lo, pe); hi = Math.max(hi, pe)
    }
  }
  return lo <= hi ? [lo, hi] : null
}

/**
 * TAIL layout (Matt, 2026-08-11): tails are the framing of the EAVES — independent
 * of the roof's own field-rafter module. Their spacing is a MAX: a member at EACH
 * END of the eave edge, the rest distributed EQUALLY between them, actual spacing =
 * span/bays ≤ the max. Exported for the unit test.
 */
export function tailStations(lo: number, hi: number, maxSpacing: number): number[] {
  const span = hi - lo
  if (!(span >= 0) || !(maxSpacing > 0)) return []
  if (span < 1e-9) return [(lo + hi) / 2]
  const bays = Math.max(1, Math.ceil(span / maxSpacing - 1e-9))
  const out: number[] = []
  for (let k = 0; k <= bays; k++) out.push(lo + (span * k) / bays)
  return out
}

/**
 * OUTLOOKER COUNT (2026-10-09): exactly `n` stations, equally spaced from `lo`. Open at the top
 * (`ridgeShared` — a paired gable's centered peak bracket at `hi` is the n+1th member, so the n
 * brackets and the peak share one bay); otherwise both ends carry a bracket (one alone sits at the
 * middle). Exported for the unit test.
 */
export function countStations(lo: number, hi: number, n: number, ridgeShared: boolean): number[] {
  const span = hi - lo
  if (!(span >= 0) || !(n >= 1)) return []
  if (ridgeShared) return Array.from({ length: n }, (_, k) => lo + (span * k) / n)
  if (n === 1 || span < 1e-9) return [(lo + hi) / 2]
  return Array.from({ length: n }, (_, k) => lo + (span * k) / (n - 1))
}

/**
 * FRAMING SESSION Phase 2 — TAIL CLASH AVOIDANCE (Matt: "tail spacing is
 * paramount because they are decorative"; 24″ rafters vs 16″ tails collide
 * every 48″): tails OWN their positions, field rafters YIELD. A rafter center
 * within clearance (the two half-widths + a 1.5″ gap) of any tail SHIFTS
 * sideways to the nearest clear position — inside its own bay (≤ half the
 * field spacing from where the module put it), inside the layout extents, and
 * clear of every tail AND every already-placed rafter. A rafter that cannot
 * clear is OMITTED (the caller logs it — the no-silent-caps rule); the tail
 * rhythm never moves. Mirrors the real framer's adjustment. Exported for the
 * unit test.
 */
export function shiftRaftersClearOfTails(
  centers: number[], tails: number[], rafterW: number, tailW: number,
  spacing: number, lo: number, hi: number,
): { centers: number[]; omitted: number } {
  const clash = (rafterW + tailW) / 2 + TAIL_CLEAR_GAP_FT
  const out: number[] = []
  let omitted = 0
  for (const c of centers) {
    if (tails.every((tc) => Math.abs(c - tc) >= clash - 1e-9)) { out.push(c); continue }
    const cands = tails.flatMap((tc) => [tc - clash, tc + clash])
      .filter((x) => x >= lo - 1e-9 && x <= hi + 1e-9
        && Math.abs(x - c) <= spacing / 2 + 1e-9
        && tails.every((tc) => Math.abs(x - tc) >= clash - 1e-9)
        && out.every((oc) => Math.abs(x - oc) >= rafterW - 1e-9))
      .sort((x, y) => Math.abs(x - c) - Math.abs(y - c))
    if (cands.length) out.push(cands[0])
    else omitted++
  }
  return { centers: out, omitted }
}

/** FRAMING SESSION Phase 2: what a tails-face field rafter derives its true
 *  extents from — the HOST wall's structural core width (the birdsmouth seat
 *  spans the plate) and its outside-of-core sheathing thickness (the plumb end
 *  cut hides just behind the outer sheathing face). */
export interface HostWallOffsets { coreFt: number; sheathFt: number }

/** the contiguous substrate/membrane thickness immediately OUTSIDE the core —
 *  the wall's sheathing stack (finish/cladding layers beyond it don't count) */
export function sheathingOutsideCoreFt(el: ResolvedElement): number {
  const a = el.assembly
  if (!a || !a.layers.length) return 0
  const s = Math.min(Math.max(a.coreBoundary?.start ?? 0, 0), a.layers.length - 1)
  let th = 0
  for (let i = s - 1; i >= 0; i--) {
    const fn = a.layers[i].function
    if (fn === 'substrate' || fn === 'membrane') th += a.layers[i].thicknessFt || 0
    else break
  }
  return th
}

/**
 * FRAMING SESSION Phase 2 — resolve the HOST WALL behind each bearing edge
 * (the doctrine: bearing intent is declared by modeled overlap). Candidates are
 * walls that themselves declare attachment to THIS roof (picked topAttachIds,
 * or the legacy blanket topAttach:'roof'); a face's host is the candidate with
 * a straight path segment PARALLEL to the face's bearing line, within the
 * wall's own thickness of it, overlapping the bearing span — nearest wins. No
 * resolvable host = null, and the face's rafters keep the pre-round
 * die-at-the-wall-line behavior (never invented geometry).
 */
export function hostWallOffsetsFor(
  roof: ResolvedElement, solved: ResolvedRoofSolve, byId?: Map<string, ResolvedElement>,
): (f: RoofFace) => HostWallOffsets | null {
  if (!byId) return () => null
  const walls: ResolvedElement[] = []
  byId.forEach((e) => {
    if (e.category !== 'wall') return
    const ids = e.params.topAttachIds
    const picked = Array.isArray(ids) && ids.length > 0
    if (picked ? (ids as unknown[]).includes(roof.id) : e.params.topAttach === 'roof') walls.push(e)
  })
  const memo = new Map<RoofFace, HostWallOffsets | null>()
  return (f: RoofFace) => {
    const hit = memo.get(f)
    if (hit !== undefined) return hit
    const e: Pt2 = [-f.uphill[1], f.uphill[0]]
    const span = spanOnBearingLine(solved.ring, f.origin, f.uphill, e)
    let best: { d: number; off: HostWallOffsets } | null = null
    for (const w of walls) {
      const path = w.params.path as Path2D | null
      if (!path || path.nodes.length < 2) continue
      const zones = assemblyZonesFt(w)
      if (zones.coreFt <= 0) continue
      const tol = Math.max(w.assemblyThicknessFt, 1)
      const n = path.closed ? path.nodes.length : path.nodes.length - 1
      for (let i = 0; i < n; i++) {
        const A = path.nodes[i].pt, B = path.nodes[(i + 1) % path.nodes.length].pt
        const len = Math.hypot(B[0] - A[0], B[1] - A[1])
        if (len < 1e-6) continue
        const dir: Pt2 = [(B[0] - A[0]) / len, (B[1] - A[1]) / len]
        if (Math.abs(dir[0] * f.uphill[0] + dir[1] * f.uphill[1]) > 0.02) continue
        const mid: Pt2 = [(A[0] + B[0]) / 2, (A[1] + B[1]) / 2]
        const dist = Math.abs((mid[0] - f.origin[0]) * f.uphill[0] + (mid[1] - f.origin[1]) * f.uphill[1])
        if (dist > tol) continue
        if (span) {
          const eA = A[0] * e[0] + A[1] * e[1], eB = B[0] * e[0] + B[1] * e[1]
          if (Math.max(eA, eB) < span[0] - 0.5 || Math.min(eA, eB) > span[1] + 0.5) continue
        }
        if (!best || dist < best.d) {
          best = { d: dist, off: { coreFt: zones.coreFt, sheathFt: sheathingOutsideCoreFt(w) } }
        }
      }
    }
    const off = best?.off ?? null
    memo.set(f, off)
    return off
  }
}

/**
 * The derived framing + tails for a solved roof — null when nothing is enabled.
 * GL2 Phase A (docs/bim-plan/12 D1): TWO merged meshOnly solids (core-layer
 * material) — the framing STUDY (field rafters + ridge/hip/valley members —
 * the INTERIOR portions buried inside the mass) carries `framing: true` and
 * leaves the 2D pipeline exactly like wall/floor framing; the SHOW members
 * (tails, outlookers, barge, AND — as of the ELEVATION FRAMING POLICY round,
 * 2026-08-21 — the open-eave EXPOSED field-rafter/hip/valley ends) carry no
 * flag and stay 2D-visible as real architecture (Matt's resolved Q2 — an
 * exposed tail must keep appearing in sections/elevations; open-eave ends are
 * the identical case). Both keep the
 * same `…|framing` groupKey, so the 3D chunk and its Systems classification are
 * byte-identical to the old single solid. Every member — show geometry AND field
 * framing buried inside the solid alike — contributes its own
 * outline edges (prism()'s `edges` param) so framing-only view reads outlined
 * throughout, not just on the exposed show members.
 * `join` (E5 r2): the roof-to-roof join context. The joiner ADOPTS the target's
 * member SETTINGS (the style authority); ALIGNED eaves lay their tail stations
 * over the MERGED span in one rhythm (each side emits its own stretch, target
 * owns overlaps); rakes carry barge segments + bracket stations only on their
 * EXPOSED stretches (r5 — seam-coverage intervals, both sides, so a partially
 * covered rake keeps its members above the joint and drops them beneath it).
 * Tails need no coverage test — they live on the CLIPPED face rings, so the
 * body join resolves them by geometry. Field framing is untouched (it lives
 * inside the mass; composition is about the SHOW members).
 */
export function buildRoofFraming(
  el: ResolvedElement, solved: ResolvedRoofSolve, join: RoofJoinCtx | null = null,
  // FRAMING SESSION Phase 2: the host-wall resolver (hostWallOffsetsFor) — null
  // (tests, headless callers) keeps tails-face rafters dying at the wall line
  hostWallFor: ((f: RoofFace) => HostWallOffsets | null) | null = null,
  // FRAMING SESSION Phase 4: the Roof Framing SYSTEM check — defined = the
  // single authority over body framing; undefined = the per-roof showFraming
  // study param governs (pre-round behavior, tests unchanged)
  roofFramingOn?: boolean,
  // FRIEZE BOARD (2026-10-07): the fascia's resolved finish (trim.ts#fasciaFinishFor —
  // handed in, since framing.ts can't import trim.ts) — the frieze wears it
  friezeFinish?: { materialId: string; surfaceMaterialId?: string } | null,
): ElementSolid[] | null {
  if (solved.flat) return null                     // flat framing (joists) deferred
  if (!clipperReady()) return null
  // VERTICAL CUTS (2026-10-08): the faces arrive already cut (generate3d); the loops are
  // read again here only so a tail is dropped whole rather than sliced at a cut line
  const cutRings = roofCutRings(el)
  const showFraming = roofFramingOn ?? (el.params.showFraming === true)
  const anyTails = solved.faces.some((f) => f.tails)
  // OPEN eaves (eave-styling round): the eave-zone structure is EXPRESSED — exposed
  // field-rafter/hip/valley ends render always, without the framing-study toggle.
  // (One-line read of the instance mode; the underside evaluator gates the same
  // flag through roofEavesOpen — flat is already excluded above.)
  const open = el.params.eaveMode === 'open'
  // FLAT SOFFIT + CORBELS round (2026-10-07): a tails edge under a FLAT soffit expresses
  // its tails as CORBELS (level brackets under the soffit — emitCorbels below); the
  // field rafters there keep the boxed-eave (E1) treatment, since the soffit hides them
  const tailsFace = (f: RoofFace) => !!f.tails && !f.flatSoffit
  // E4: rake outlookers + barge — show members like tails (a barge spec with no
  // rake to land on, e.g. bargeDefault on a pure hip, emits nothing and the empty
  // accumulator still returns null)
  const anyBarge = solved.specs.some((s) => s.barge)
  if (!showFraming && !anyTails && !open && !anyBarge) return null

  const t = el.assemblyThicknessFt
  if (t <= 0) return null
  const zones = assemblyZonesFt(el)
  if (zones.coreFt <= 0) return null
  const bearing = solved.ring
  const deckFt = deckingFt(el)

  // rafters = the TYPE's core declaration (ROOF CORE DEFINITION round — wood lumber
  // or TJI at flange width, one read shared with the assembly-core derivation);
  // spacing is an INSTANCE param now; tails = instance eave styling
  const rafter = roofRafterMemberSize(el.params)
  const rafterSpacingFt = (Number(el.params?.rafterSpacingIn) || 24) / 12
  // E5 r2 ADOPTION: a joined roof reads its eave-member SETTINGS from the join
  // target (the style authority) so a merged run can never mismatch. The on/off
  // TOGGLES stay per-roof — they ride the specs and drive the mass's decking
  // read, which byId-less consumers (underside, walls-attach) derive alone.
  const src = join?.authority ?? el
  const tail = lumberSize(src.params.tailSize, TAIL_SIZES) ?? TAIL_SIZES[5]
  const tailSpacingFt = Math.max(1, Number(src.params.tailSpacingIn) || 24) / 12
  const profile = tailProfile(src.params.tailStyle)

  const rafterWFt = rafter.widthIn / 12
  const rafterDFt = Math.min(rafter.depthIn / 12, zones.coreFt)
  const tailWFt = tail.widthIn / 12
  const tailDFt = tail.depthIn / 12

  // D1 split: study members and show members accumulate into SEPARATE solids.
  // `acc`/`edges` retarget from study to show right before the tails section —
  // every emitter below reads the binding at call time, so the section order
  // (study block first, tails/outlookers/barge after) is the routing.
  const accStudy = newMeshAcc()
  const edgesStudy: number[] = []
  const accShow = newMeshAcc()
  const edgesShow: number[] = []
  // MEMBER PAINT (2026-10-05): the decorative show members each get their own solid
  // (tails / outlookers / barge) so the Paint Bucket can coat one kind on a roof —
  // accShow keeps the open-eave exposed rafter ends
  const accTails = newMeshAcc()
  const edgesTails: number[] = []
  const accOut = newMeshAcc()
  const edgesOut: number[] = []
  const accBarge = newMeshAcc()
  const edgesBarge: number[] = []
  // FRIEZE BOARD: its own piece — trim, keyed as fascia for the Paint Brush
  const accFrieze = newMeshAcc()
  const edgesFrieze: number[] = []
  let acc: MeshAcc = accStudy
  let edges: number[] = edgesStudy
  // FRAMING ENVELOPE (envelope.ts): the region each member kind sweeps, recorded beside
  // the members from the same closures — the section's assembly-depth tone reads it
  const envelope: RoofEnvelopePiece[] = []
  const ccwRing = (r: Pt2[]): Pt2[] => (signedArea(r) < 0 ? [...r].reverse() : r)

  /**
   * FRAMING SESSION Phase 2: the tail stations a face actually EMITS — the one
   * computation both the tails emitter and the field-rafter clash avoidance
   * read, so a rafter yields to exactly the tails that exist. Everything here
   * is the E2/E5 station logic verbatim (bearing-span axis, the E5 r2 merged
   * rhythm across an aligned join, the hip-corner tip probe), just hoisted out
   * of the tails loop.
   */
  const emittedTailStations = (f: RoofFace, fr: FaceFrame): number[] => {
    if (!f.tails) return []
    const ov = f.overhangFt ?? 0
    if (!(ov > 0.05)) return []
    const { d, e } = fr
    const faceSpan = spanOnBearingLine(f.ring, f.origin, f.uphill, e)
    const wallSpan = spanOnBearingLine(solved.ring, f.origin, f.uphill, e)
    if (!faceSpan || !wallSpan) return []
    const w0 = Math.max(faceSpan[0], wallSpan[0])
    const w1 = Math.min(faceSpan[1], wallSpan[1])
    if (!(w1 - w0 > tailWFt)) return []
    const originE = f.origin[0] * e[0] + f.origin[1] * e[1]

    // E5 r2 MERGED RHYTHM (Matt: "running all the way out to the end of the
    // joined eaves, when they are in alignment"): an ALIGNED eave lays its
    // stations over the UNION of both roofs' bearing spans on the shared line —
    // ONE end-to-end array across the joint. Each roof emits only the stations
    // on its own segment; the TARGET owns any overlap (both sides compute the
    // identical array — the target derives the join in reverse). Merging only
    // engages when the partner's edge actually runs tails, else this roof lays
    // its own full run as ever. r3: the entry is matched GEOMETRICALLY — the
    // face's origin sits on its own bearing edge and uphill is its inward
    // normal. (Historically forced: f.edgeIndex leaked the solver's NORMALIZED
    // spec order, wrong on CW-drawn boundaries — the E4 rake mapping's winding
    // lesson. 2026-08-19: edgeIndex is remapped to input order at the solver
    // boundary now; the geometric match stays as belt-and-suspenders.)
    const ae = join?.aligned.find((en) =>
      Math.abs(en.ownD[0] * (f.origin[1] - en.ownA[1]) - en.ownD[1] * (f.origin[0] - en.ownA[0])) <= 1e-3
      && f.uphill[0] * en.ownInward[0] + f.uphill[1] * en.ownInward[1] > 0.999)
    let sts: number[]
    if (ae && ae.otherTails) {
      const proj = (p: Pt2) => p[0] * e[0] + p[1] * e[1]
      const o0 = Math.min(proj(ae.otherA), proj(ae.otherB))
      const o1 = Math.max(proj(ae.otherA), proj(ae.otherB))
      const oS0 = Math.min(proj(ae.otherSheetA), proj(ae.otherSheetB))
      const oS1 = Math.max(proj(ae.otherSheetA), proj(ae.otherSheetB))
      // the merged array spans the union of BEARING extents (members land at
      // the run's true wall-corner ends)…
      const u0 = Math.min(w0, o0), u1 = Math.max(w1, o1)
      // …but each roof's OWN emit range relaxes to its SHEET edge on the
      // partner side (r4, Matt's junction gap): the run continues across a
      // rake band toward the joint — its owner emits there, and the joiner's
      // exclusion is the partner's SHEET extent, so no union station between
      // the wall corner and the seam is orphaned
      const partnerHigh = (oS0 + oS1) / 2 > (w0 + w1) / 2
      const e0r = partnerHigh ? w0 : faceSpan[0]
      const e1r = partnerHigh ? faceSpan[1] : w1
      sts = tailStations(u0 + tailWFt / 2, u1 - tailWFt / 2, tailSpacingFt).filter((c) =>
        c >= e0r - 1e-6 && c <= e1r + 1e-6
        && (!ae.otherPrimary || c < oS0 - 1e-6 || c > oS1 + 1e-6))
    } else {
      sts = tailStations(w0 + tailWFt / 2, w1 - tailWFt / 2, tailSpacingFt)
    }
    // a tail whose tip leaves this face's plan region (hip corners — and,
    // since E5 r2, the JOIN-CLIPPED region: the body clip shrank f.ring to
    // the valley, so members resolve to the exposed roof by geometry) is
    // never emitted — and never repels a rafter
    return sts.filter((c) => {
      const Wp: Pt2 = [f.origin[0] + e[0] * (c - originE), f.origin[1] + e[1] * (c - originE)]
      const tipProbe: Pt2 = [Wp[0] + d[0] * ov * 0.98, Wp[1] + d[1] * ov * 0.98]
      if (!pointInRing(tipProbe, f.ring)) return false
      // VERTICAL CUTS (verticalCuts.ts): a tail touching a cut anywhere along its exposed
      // length or width is dropped WHOLE — never left half-sliced at the cut line
      if (cutRings.length) {
        for (const k of [0.02, 0.5, 0.98]) {
          for (const w of [-tailWFt / 2, 0, tailWFt / 2]) {
            const q: Pt2 = [Wp[0] + d[0] * ov * k + e[0] * w, Wp[1] + d[1] * ov * k + e[1] * w]
            if (cutRings.some((r) => pointInRing(q, r))) return false
          }
        }
      }
      return true
    })
  }

  /**
   * FLAT SOFFIT + CORBELS round (2026-10-07, Matt: "the rafter tails then become corbels
   * that sit under the flat soffit and don't need to extend back into the building
   * framing"): on a tails edge under a FLAT soffit, each tail station carries a CORBEL
   * instead — a LEVEL member of the tail stock, top against the flat soffit
   * (soffit.ts#flatSoffitZ — the same elevation the mass's soffit is built at), running
   * from the bearing wall's OUTSIDE face (f.wallFt — the ROOF BEARING WALLS read; with
   * no wall standing under the edge, the bearing line, buried in whatever is there) out
   * to the fascia's back face (the roof edge itself without a fascia), its outer end
   * shaped by the tail style over the PROFILE RUN RULE's 2× depth. Same stations as
   * the tails (the merged rhythm across a join included), same paint chunk ('tails').
   * No back span: a corbel is a bracket, not a rafter.
   */
  const fasciaThFt = (lumberSize(src.params.fasciaSize, FASCIA_SIZES) ?? FASCIA_SIZES[3]).widthIn / 12
  /**
   * FRIEZE BOARD (2026-10-07, Matt: "a toggle to turn on/off the frieze board between
   * tails/corbels and to set the frieze board's size"): the trim board on the wall's
   * OUTSIDE face (wallFt — the finish face; no wall = the bearing line) along a tails
   * eave, across the face's bearing span. Under SLOPED tails it is NOTCHED around each
   * tail (the tails run through the wall plane) and its top is cut to the decking's
   * slope; under a FLAT soffit it runs CONTINUOUS behind the corbels with its top against
   * the soffit, and the corbels mount over it (their inner end moves out by its
   * thickness). Settings read from the join authority like every eave member; the finish
   * is the fascia's (handed in by the roof generator), so the two trim boards match.
   */
  const friezeBoard = lumberSize(src.params.friezeSize, FRIEZE_SIZES) ?? FRIEZE_SIZES[2]
  const friezeThFt = friezeBoard.widthIn / 12
  const friezeDpFt = friezeBoard.depthIn / 12
  const friezeOn = (f: RoofFace): boolean => !!f.tails && !!f.frieze
  const emitFrieze = (f: RoofFace, fr: FaceFrame, notchAt: number[], zTopOf: (p: Pt2) => number, zBot: number): void => {
    const { d, e } = fr
    const aW = Math.max(0, f.wallFt ?? 0)
    const faceSpan = spanOnBearingLine(f.ring, f.origin, f.uphill, e)
    const wallSpan = spanOnBearingLine(solved.ring, f.origin, f.uphill, e)
    if (!faceSpan || !wallSpan) return
    const w0 = Math.max(faceSpan[0], wallSpan[0]), w1 = Math.min(faceSpan[1], wallSpan[1])
    if (!(w1 - w0 > tailWFt)) return
    const dO = f.origin[0] * d[0] + f.origin[1] * d[1]
    const corner = (pe: number, pd: number): Pt2 => [e[0] * pe + d[0] * pd, e[1] * pe + d[1] * pd]
    const ccwOf = (r: Pt2[]): Pt2[] => (signedArea(r) < 0 ? [...r].reverse() : r)
    // TURN THE CORNERS (Matt, 2026-10-07: "the frieze boards need to turn the outer corners,
    // not stopping between the last two tails/corbels"): the bearing span ends at the
    // INTERIOR corner of the stud walls, a wall's thickness short of the outside corner the
    // board must reach. Run each end on past the corner and let the face ring cut it — at a
    // hip or valley the ring's edge IS the corner's bisector, so the two faces' boards meet
    // along it, mitered. At a GABLE end the rake band would keep the board going along the
    // gable wall, so it stops flush with that wall's outside face instead.
    const spec = solved.specs[f.edgeIndex]
    const nearPt = (p: Pt2, q: Pt2) => Math.hypot(p[0] - q[0], p[1] - q[1]) <= 1e-3
    const extAt = (P: Pt2 | null): number => {
      const nb = P && spec ? solved.specs.find((s) => s !== spec && (nearPt(s.a, P) || nearPt(s.b, P))) : undefined
      if (nb?.gable) return Math.max(0, nb.wallFt ?? 0)
      return aW + friezeThFt + 0.25
    }
    let P0: Pt2 | null = null, P1: Pt2 | null = null
    if (spec) {
      const eA = spec.a[0] * e[0] + spec.a[1] * e[1], eB = spec.b[0] * e[0] + spec.b[1] * e[1]
      ;[P0, P1] = eA <= eB ? [spec.a, spec.b] : [spec.b, spec.a]
    }
    const w0e = w0 - extAt(P0), w1e = w1 + extAt(P1)
    const band = ccwOf([corner(w0e, dO + aW), corner(w1e, dO + aW), corner(w1e, dO + aW + friezeThFt), corner(w0e, dO + aW + friezeThFt)])
    const cuts = notchAt.map((c) => ccwOf([
      corner(c - tailWFt / 2, dO + aW - 0.01), corner(c + tailWFt / 2, dO + aW - 0.01),
      corner(c + tailWFt / 2, dO + aW + friezeThFt + 0.01), corner(c - tailWFt / 2, dO + aW + friezeThFt + 0.01),
    ]))
    let rings = keepRings(intersectClosed([band], [f.ring]))
    if (cuts.length && rings.length) rings = keepRings(differenceClosed(rings, cuts))
    for (const r of rings) prism(accFrieze, r, () => zBot, zTopOf, edgesFrieze)
  }
  const emitCorbels = (f: RoofFace, fr: FaceFrame): void => {
    const soffitZ = flatSoffitZ(el, solved, f)
    if (soffitZ == null) return
    const ov = f.overhangFt ?? 0
    const { d, e } = fr
    const spec = solved.specs[f.edgeIndex]
    // the wall's outside face — over the frieze board when there is one
    const a0 = Math.max(0, f.wallFt ?? 0) + (friezeOn(f) ? friezeThFt : 0)
    const a1 = ov - (spec?.fascia ? fasciaThFt : 0)               // the fascia's back face
    if (!(a1 - a0 > tailWFt)) return
    const sts = emittedTailStations(f, fr)
    if (!sts.length) return
    // the frieze runs continuous behind the corbels, top against the soffit
    if (friezeOn(f)) emitFrieze(f, fr, [], () => soffitZ, soffitZ - friezeDpFt)
    const originE = f.origin[0] * e[0] + f.origin[1] * e[1]
    // section outline in (a, z): a = 0 at the bearing line, the member level, the
    // shaped cut at the outer end (memberEndProfile with a level top: s = 0)
    const Lp = Math.min(2 * tailDFt, a1 - a0)
    const cutPts = memberEndProfile(profile.bottom, a1, soffitZ, 0, 1, tailDFt, Lp)
    const outline: [number, number][] = []
    if (cutPts[0][0] > a0 + 1e-9) outline.push([a0, soffitZ - tailDFt])   // full-depth run to the cut
    outline.push(...cutPts)                                                // shaped bottom
    outline.push([a1, soffitZ])                                            // tip top
    outline.push([a0, soffitZ])                                            // wall-face top
    for (const c of sts) {
      const Wp: Pt2 = [f.origin[0] + e[0] * (c - originE), f.origin[1] + e[1] * (c - originE)]
      const planAt = (a: number): Pt2 => [Wp[0] + d[0] * a, Wp[1] + d[1] * a]
      emitProfiledMember(accTails, edgesTails, planAt, d, e, tailWFt / 2, outline)
    }
    // FRAMING ENVELOPE: the corbel section swept along the run of corbels (a from the
    // bearing line, the same a the outline is drawn in)
    const dO = f.origin[0] * d[0] + f.origin[1] * d[1]
    const cLo = Math.min(...sts) - tailWFt / 2, cHi = Math.max(...sts) + tailWFt / 2
    const at = (pe: number, a: number): Pt2 => [e[0] * pe + d[0] * (dO + a), e[1] * pe + d[1] * (dO + a)]
    for (const r of keepRings(intersectClosed([ccwRing([at(cLo, a0), at(cHi, a0), at(cHi, a1), at(cLo, a1)])], [f.ring]))) {
      envelope.push({ kind: 'profile', ring: r, origin: [f.origin[0], f.origin[1]], d: [d[0], d[1]], outline })
    }
  }

  /**
   * FLAT SOFFIT FRAMING (Matt, 2026-10-07: "the soffit framing to attach back to the
   * outer face of the wall sheathing with a ledger running along the joist connection
   * line"): what holds a flat soffit up. A LEDGER along the wall at the soffit's framing
   * height, its back on the OUTER FACE OF THE WALL SHEATHING — the host wall's core +
   * sheathing stack, the same read the tails' birdsmouth uses (hostWallOffsetsFor); no
   * resolvable host = the bearing wall's reach (wallFt), else the bearing line — and
   * LOOKOUTS at the rafter spacing from the ledger's face out to the back of the fascia
   * (the roof edge without one), both of the soffit framing size, sitting on the soffit
   * PANEL (soffit + the decking thickness). Depth clamps to the headroom under the
   * sheathing at the fascia line (a shallow fascia lays them flat), and the eave RAFTERS
   * above are cut to clear them — the tail cut to a fascia. Study members: shown with
   * the roof framing, buried in the solid mass otherwise. Resolved once per face.
   */
  const soffitFrame = lumberSize(el.params.soffitFramingSize, SOFFIT_FRAMING_SIZES) ?? SOFFIT_FRAMING_SIZES[0]
  const soffitFramingOf = (f: RoofFace): { a0: number; a1: number; zb: number; D: number; w0: number; w1: number; fr: FaceFrame } | null => {
    if (f.flying) return null
    const soffitZ = flatSoffitZ(el, solved, f)
    if (soffitZ == null) return null
    const fr = frameOf(f)
    if (!fr) return null
    const ov = f.overhangFt ?? 0
    const spec = solved.specs[f.edgeIndex]
    const host = hostWallFor?.(f) ?? null
    const a0 = host ? host.coreFt + host.sheathFt : Math.max(0, f.wallFt ?? 0)
    const a1 = ov - (spec?.fascia ? fasciaThFt : 0)
    const w = soffitFrame.widthIn / 12
    if (!(a1 - a0 > 2 * w)) return null
    const zb = soffitZ + Math.max(deckFt, 1 / 48)
    // headroom: the sheathing's underside at the fascia line (the lowest point of the
    // roof over the eave) — on edge when it fits, flat when it doesn't, nothing if even
    // a flat member can't
    const sheathUnder = f.zAt(f.origin) - f.m * ov + (t - zones.aboveFt) * fr.sq
    const room = sheathUnder - zb - 1 / 96
    const D = room >= soffitFrame.depthIn / 12 ? soffitFrame.depthIn / 12 : room >= w ? w : 0
    if (D <= 0) return null
    const faceSpan = spanOnBearingLine(f.ring, f.origin, f.uphill, fr.e)
    const wallSpan = spanOnBearingLine(solved.ring, f.origin, f.uphill, fr.e)
    if (!faceSpan || !wallSpan) return null
    const w0 = Math.max(faceSpan[0], wallSpan[0]), w1 = Math.min(faceSpan[1], wallSpan[1])
    if (!(w1 - w0 > w)) return null
    return { a0, a1, zb, D, w0, w1, fr }
  }
  /** the top of the soffit framing on a flat face — what the eave rafters stay above */
  const flatSoffitFramingTopZ = (f: RoofFace): number | null => {
    const s = soffitFramingOf(f)
    return s ? s.zb + s.D : null
  }

  // ---------- field rafters + skeleton members ----------
  // BODY members are the framing STUDY (showFraming); with OPEN eaves the
  // EAVE-ZONE portions render regardless — the mass no longer covers them there,
  // so the structure reads for real in 3D (Matt's eave-styling round).
  if (showFraming || open) {
    // RAKES WITH OUTLOOKERS + BARGE (2026-10-08, Matt: "I still see main body rafters extending
    // out into the rake when there are outlookers and barge rafters. They shouldn't do that."):
    // past a gable bearing line that carries a barge, the overhang's structure IS the outlookers
    // and the barge — no field rafter is laid there (its exposed piece was the whole rafter, since
    // the rake band lies outside the bearing polygon). Each line = a point on the bearing edge +
    // its outward normal.
    const bargeRakeLines = specLines(solved).filter((L) => L.spec.gable && L.spec.barge)
      .map((L) => ({ a: L.spec.a, n: L.n }))
    const pastBargeRake = (p: Pt2) => bargeRakeLines.some((L) => (p[0] - L.a[0]) * L.n[0] + (p[1] - L.a[1]) * L.n[1] > 0.01)
    // FRAMING ENVELOPE: the same "past a barge rake" test as a plan region (half-planes
    // beyond each barge rake line), subtracted from the field-rafter envelope
    const BIG = 1000
    const bargeBands: Pt2[][] = bargeRakeLines.map((L) => {
      const nl = Math.hypot(L.n[0], L.n[1]) || 1
      const n: Pt2 = [L.n[0] / nl, L.n[1] / nl], tg: Pt2 = [-n[1], n[0]]
      const p0: Pt2 = [L.a[0] + n[0] * 0.01, L.a[1] + n[1] * 0.01]
      return ccwRing([
        [p0[0] - tg[0] * BIG, p0[1] - tg[1] * BIG], [p0[0] + tg[0] * BIG, p0[1] + tg[1] * BIG],
        [p0[0] + tg[0] * BIG + n[0] * BIG, p0[1] + tg[1] * BIG + n[1] * BIG],
        [p0[0] - tg[0] * BIG + n[0] * BIG, p0[1] - tg[1] * BIG + n[1] * BIG],
      ])
    })
    for (const f of solved.faces) {
      // TEAR r6 (Matt's "funky cleanup" screenshots): a FLYING RAKE strip is
      // rake OVERHANG, not a roof body — its structure is the outlookers and
      // barge, never field rafters. Framing it like a face laid rafters with
      // an "eave" treatment at the kiss line (mid-roof), the reported sawtooth
      // and reduced-depth-in-the-wrong-place.
      if (f.flying) continue
      const fr = frameOf(f)
      if (!fr) continue
      const wantEave = !tailsFace(f) && (showFraming || (open && !f.flatSoffit))
      if (!showFraming && !wantEave) continue
      const { d, e, sq } = fr
      const belowDv = zones.belowFt * sq
      const depthDv = rafterDFt * sq
      const zBot = (p: Pt2) => f.zAt(p) + belowDv
      const zTop = (p: Pt2) => f.zAt(p) + belowDv + depthDv

      // strip extents from the face ring projected onto the plate (e) and run (d) axes
      let e0 = Infinity, e1 = -Infinity, d0 = Infinity, d1 = -Infinity
      for (const p of f.ring) {
        const pe = p[0] * e[0] + p[1] * e[1]
        const pd = p[0] * d[0] + p[1] * d[1]
        e0 = Math.min(e0, pe); e1 = Math.max(e1, pe)
        d0 = Math.min(d0, pd); d1 = Math.max(d1, pd)
      }
      if (!(e1 - e0 > rafterWFt)) continue

      // E1 interleave: eave-zone rafters thin to the edge's eave structure depth;
      // on a TAILS face the field rafters instead get their TRUE extents
      // (FRAMING SESSION Phase 2): a BIRDSMOUTH at the plate — seat flat at the
      // bearing height (Phase 1's plate line, where the level double plate tops
      // out), heel plumb at the host wall's outer core face — continuing to a
      // PLUMB CUT just behind the wall's outer SHEATHING face, both derived from
      // the host wall's own assembly. No resolvable host wall = the pre-round
      // die-at-the-wall-line (no invented wall dimensions).
      const eaveDepthFt = f.eaveDepthIn != null && Number.isFinite(f.eaveDepthIn)
        ? Math.min(f.eaveDepthIn / 12, rafterDFt)
        : null
      const cropRafters0 = !tailsFace(f) && eaveDepthFt != null && eaveDepthFt < rafterDFt - 1e-9
      const zBotEave0 = (p: Pt2) => zTop(p) - (eaveDepthFt ?? rafterDFt) * sq
      // FLAT SOFFIT FRAMING: the eave rafters are CUT to clear the soffit framing (the
      // tail cut to a fascia) — never below it, never above their own top
      const sTop = f.flatSoffit ? flatSoffitFramingTopZ(f) : null
      const cropRafters = cropRafters0 || sTop != null
      const zBotEave = sTop == null ? zBotEave0
        : (p: Pt2) => Math.min(Math.max(zBotEave0(p), sTop), zTop(p) - 1 / 48)

      const host = tailsFace(f) ? (hostWallFor?.(f) ?? null) : null
      const dOrigin = f.origin[0] * d[0] + f.origin[1] * d[1]
      const seatZ = f.zAt(f.origin)               // the plate line (bearing height)
      // fix round (Matt, 2026-08-13): the rafter ends plumb AT the outer face of
      // the wall's FRAMING (core) layer — never past it into the sheathing. The
      // heel and the end cut are the same plumb face.
      const cutEnd = host ? host.coreFt : 0
      const corner = (pe: number, pd: number): Pt2 => [e[0] * pe + d[0] * pd, e[1] * pe + d[1] * pd]
      /** a downhill band [a0, a1] measured from the bearing line, across the strip axis */
      const bandRect = (a0: number, a1: number): Pt2[] => [
        corner(e0 - 1, dOrigin + a0), corner(e1 + 1, dOrigin + a0),
        corner(e1 + 1, dOrigin + a1), corner(e0 - 1, dOrigin + a1),
      ]

      // FRAMING ENVELOPE: every branch of the per-rafter emit below, swept across the
      // whole face (the study only — open-eave ends alone leave the body solid, untoned)
      if (showFraming) {
        const pl = planeOf(f.zAt, f.origin)
        const full = { botOff: belowDv, topOff: belowDv + depthDv }
        const put = (rs: Pt2[][], z: { botOff: number; topOff: number; botFloor?: number }) => {
          for (const r of keepRings(rs)) envelope.push({ kind: 'plane', ring: r, ...pl, ...z })
        }
        let region = keepRings([ccwRing(f.ring)])
        if (bargeBands.length) region = keepRings(differenceClosed(region, bargeBands))
        if (region.length) {
          if (tailsFace(f)) {
            if (host && cutEnd > 1e-6) {
              put(intersectClosed(region, [ccwRing(bandRect(d0 - dOrigin - 1, 0))]), full)
              put(intersectClosed(region, [ccwRing(bandRect(0, cutEnd))]), { ...full, botFloor: seatZ })
            } else {
              put(intersectClosed(region, [bearing]), full)
            }
          } else if (cropRafters) {
            const eave = { botOff: belowDv + depthDv - (eaveDepthFt ?? rafterDFt) * sq, topOff: full.topOff, ...(sTop != null ? { botFloor: sTop } : {}) }
            put(intersectClosed(region, [bearing]), full)
            put(differenceClosed(region, [bearing]), eave)
          } else {
            put(region, full)
          }
        }
      }

      // FRAMING SESSION Phase 2 — tail clash avoidance: the field module yields
      // to the decorative tails (shiftRaftersClearOfTails); dropped rafters are
      // never silent
      const tailsAt = tailsFace(f) ? emittedTailStations(f, fr) : []
      let rafCenters = stations(e0 + rafterWFt / 2, e1 - rafterWFt / 2, rafterSpacingFt)
      if (tailsAt.length) {
        const adj = shiftRaftersClearOfTails(
          rafCenters, tailsAt, rafterWFt, tailWFt, rafterSpacingFt,
          e0 + rafterWFt / 2, e1 - rafterWFt / 2,
        )
        rafCenters = adj.centers
        if (adj.omitted > 0) {
          console.warn(`[modeler] roof ${el.id}: ${adj.omitted} field rafter(s) could not clear a decorative tail within their bay and were omitted (the tail rhythm governs)`)
        }
      }

      for (const c of rafCenters) {
        // a rafter standing in a barge rake's overhang band isn't framed at all
        if (bargeRakeLines.length && pastBargeRake(corner(c, dOrigin - 0.05))) continue
        const lo = c - rafterWFt / 2, hi = c + rafterWFt / 2
        const strip: Pt2[] = [corner(lo, d0 - 1), corner(hi, d0 - 1), corner(hi, d1 + 1), corner(lo, d1 + 1)]
        let rings = keepRings(intersectClosed([strip], [f.ring]))
        if (!rings.length) continue
        if (tailsFace(f)) {
          if (host && cutEnd > 1e-6) {
            // two bands, welded into one member: sloped body inboard of the
            // bearing line; the SEAT (bottom flat at the plate line — the
            // birdsmouth, notch depth = wherever the sloped underside dips
            // below it over the plate) out to the plumb END CUT at the wall
            // core's outer face. The band boundary at the bearing line emits
            // no line (stripSeamEdges) — one uniform rafter, per Matt's
            // 2026-08-13 report.
            const seatBot = (p: Pt2) => Math.min(Math.max(zBot(p), seatZ), zTop(p))
            const memberEdges: number[] = []
            for (const r of keepRings(intersectClosed(rings, [bandRect(d0 - dOrigin - 1, 0)]))) prism(acc, r, zBot, zTop, memberEdges)
            for (const r of keepRings(intersectClosed(rings, [bandRect(0, cutEnd)]))) prism(acc, r, seatBot, zTop, memberEdges)
            edges.push(...stripSeamEdges(memberEdges, d, dOrigin))
          } else {
            rings = keepRings(intersectClosed(rings, [bearing]))
            for (const r of rings) prism(acc, r, zBot, zTop, edges)
          }
        } else if (!showFraming) {
          // OPEN eaves with the study off: only the EXPOSED eave-zone ends — the
          // body stays inside the mass; ends thin to the E1 depth when set.
          // ELEVATION FRAMING POLICY round (2026-08-21, Matt's allowlist —
          // "rafters in open eaves" are exposed architecture): route straight
          // into accShow/edgesShow instead of the ambient acc/edges (still
          // accStudy here — the retarget below only flips it AFTER this whole
          // per-face loop), so they're never classified alongside hidden studs.
          for (const r of keepRings(differenceClosed(rings, [bearing]))) prism(accShow, r, zBotEave, zTop, edgesShow)
        } else if (cropRafters) {
          for (const r of keepRings(intersectClosed(rings, [bearing]))) prism(acc, r, zBot, zTop, edges)
          for (const r of keepRings(differenceClosed(rings, [bearing]))) prism(acc, r, zBotEave, zTop, edges)
        } else {
          for (const r of rings) prism(acc, r, zBot, zTop, edges)
        }
      }
    }

    // ridge / hip / valley members along the classified skeleton edges
    const sk = roofSkeletonFor(el)
    if (sk) {
      for (const edge of sk.edges) {
        if (edge.kind !== 'ridge' && edge.kind !== 'hip' && edge.kind !== 'valley') continue
        const ax = edge.a[0], ay = edge.a[1], bx = edge.b[0], by = edge.b[1]
        const dx = bx - ax, dy = by - ay
        const len = Math.hypot(dx, dy)
        if (len < rafterWFt) continue
        const ux = dx / len, uy = dy / len
        const nx = -uy * (rafterWFt / 2), ny = ux * (rafterWFt / 2)
        const face = sk.faces[edge.faces[0]]
        const sq = Math.sqrt(1 + face.plane.risePerRun * face.plane.risePerRun)
        const topOff = zones.belowFt * sq + rafterDFt * sq
        const depthDv = rafterDFt * sq
        // z along the edge (linear), constant across the member width
        const zAt = (p: Pt2) => {
          const s = ((p[0] - ax) * ux + (p[1] - ay) * uy) / len
          return edge.a[2] + (edge.b[2] - edge.a[2]) * Math.max(0, Math.min(1, s))
        }
        const ring: Pt2[] = [
          [ax - nx, ay - ny], [bx - nx, by - ny], [bx + nx, by + ny], [ax + nx, ay + ny],
        ]
        const ccw = signedArea(ring) > 0 ? ring : [...ring].reverse()
        const zTopM = (p: Pt2) => zAt(p) + topOff
        const zBotM = (p: Pt2) => zAt(p) + topOff - depthDv
        // hips/valleys cross the bearing line into the eave band — they follow the
        // SAME E1/tails treatment as the field rafters (a corner would otherwise
        // betray the crop): tails face = die at the wall line; E1 crop = eave
        // portion thins to the edge's eave structure depth
        const sf = solved.faces[edge.faces[0]]
        const memberEaveFt = sf && !tailsFace(sf) && sf.eaveDepthIn != null && Number.isFinite(sf.eaveDepthIn)
          ? Math.min(sf.eaveDepthIn / 12, rafterDFt)
          : null
        if (sf && tailsFace(sf)) {
          if (showFraming) {
            for (const r of keepRings(intersectClosed([ccw], [bearing]))) prism(acc, r, zBotM, zTopM, edges)
          }
        } else if (!showFraming) {
          // OPEN eaves with the study off: exposed hip/valley ENDS only (ridges lie
          // fully inside the bearing polygon and drop out of the difference).
          // ELEVATION FRAMING POLICY round (2026-08-21): same exposed-member
          // routing as the field-rafter branch above — accShow/edgesShow, not
          // the ambient (still-study) acc/edges.
          // (a flat soffit boxes the eave — nothing exposed there)
          if (!sf?.flatSoffit) for (const r of keepRings(differenceClosed([ccw], [bearing]))) {
            prism(accShow, r, (p) => zTopM(p) - (memberEaveFt ?? rafterDFt) * sq, zTopM, edgesShow)
          }
        } else if ((memberEaveFt != null && memberEaveFt < rafterDFt - 1e-9) || (sf?.flatSoffit && flatSoffitFramingTopZ(sf) != null)) {
          // (a hip/valley over a FLAT soffit stays above the soffit framing like the rafters)
          const sTopM = sf?.flatSoffit ? flatSoffitFramingTopZ(sf) : null
          const zBotE = (p: Pt2) => {
            const b = zTopM(p) - (memberEaveFt ?? rafterDFt) * sq
            return sTopM == null ? b : Math.min(Math.max(b, sTopM), zTopM(p) - 1 / 48)
          }
          for (const r of keepRings(intersectClosed([ccw], [bearing]))) prism(acc, r, zBotM, zTopM, edges)
          for (const r of keepRings(differenceClosed([ccw], [bearing]))) {
            prism(acc, r, zBotE, zTopM, edges)
          }
        } else {
          prism(acc, ccw, zBotM, zTopM, edges)
        }
      }
    }
  }

  // ---------- FLAT SOFFIT FRAMING (study members — the ledger + lookouts) ----------
  if (showFraming) {
    for (const f of solved.faces) {
      const s = soffitFramingOf(f)
      if (!s) continue
      const { d, e } = s.fr
      const w = soffitFrame.widthIn / 12
      const dOrigin = f.origin[0] * d[0] + f.origin[1] * d[1]
      const corner = (pe: number, pd: number): Pt2 => [e[0] * pe + d[0] * pd, e[1] * pe + d[1] * pd]
      const ccwOf = (r: Pt2[]): Pt2[] => (signedArea(r) < 0 ? [...r].reverse() : r)
      const zBot = () => s.zb, zTopS = () => s.zb + s.D
      // the ledger: along the wall, its back on the sheathing's outer face
      const ledger = ccwOf([corner(s.w0, dOrigin + s.a0), corner(s.w1, dOrigin + s.a0), corner(s.w1, dOrigin + s.a0 + w), corner(s.w0, dOrigin + s.a0 + w)])
      for (const r of keepRings(intersectClosed([ledger], [f.ring]))) prism(acc, r, zBot, zTopS, edges)
      // the lookouts: from the ledger's face to the back of the fascia, at the rafter spacing
      for (const c of stations(s.w0 + w / 2, s.w1 - w / 2, rafterSpacingFt)) {
        const look = ccwOf([corner(c - w / 2, dOrigin + s.a0 + w), corner(c + w / 2, dOrigin + s.a0 + w), corner(c + w / 2, dOrigin + s.a1), corner(c - w / 2, dOrigin + s.a1)])
        for (const r of keepRings(intersectClosed([look], [f.ring]))) prism(acc, r, zBot, zTopS, edges)
      }
      // FRAMING ENVELOPE: ledger + lookouts as one level band, a from the bearing line
      const band = ccwOf([corner(s.w0, dOrigin + s.a0), corner(s.w1, dOrigin + s.a0), corner(s.w1, dOrigin + s.a1), corner(s.w0, dOrigin + s.a1)])
      for (const r of keepRings(intersectClosed([band], [f.ring]))) {
        envelope.push({
          kind: 'profile', ring: r, origin: [f.origin[0], f.origin[1]], d: [d[0], d[1]],
          outline: [[s.a0, s.zb], [s.a1, s.zb], [s.a1, s.zb + s.D], [s.a0, s.zb + s.D]],
        })
      }
    }
  }

  // ---------- decorative tails (show members — independent of showFraming) ----------
  // D1: everything from here down is SHOW members — retarget the accumulators
  acc = accShow
  edges = edgesShow
  for (const f of solved.faces) {
    if (!f.tails) continue
    const fr = frameOf(f)
    if (!fr) continue
    // FLAT SOFFIT + CORBELS round: under a flat soffit the tails are corbels
    if (f.flatSoffit) { emitCorbels(f, fr); continue }
    const ov = f.overhangFt ?? 0
    if (!(ov > 0.05)) continue                    // no eave depth = nothing to express
    const { d, e, sq } = fr
    const L = 3 * ov                              // 2× reach-in + the exposed eave depth
    const topOffDv = (zones.belowFt + zones.coreFt - deckFt) * sq   // decking underside
    const depthDv = tailDFt * sq

    // The station layout (the bearing-span axis + the shed-face crossings lesson,
    // the E5 r2/r3/r4 merged rhythm across an aligned join, the hip-corner tip
    // probe) lives in emittedTailStations — hoisted verbatim in the FRAMING
    // SESSION Phase 2 refactor so the field-rafter CLASH AVOIDANCE reads the
    // IDENTICAL array these tails emit from.
    const tailsAtStations = emittedTailStations(f, fr)
    if (!tailsAtStations.length) continue
    const originE = f.origin[0] * e[0] + f.origin[1] * e[1]
    // FRIEZE BOARD: on the wall face between the tails, top cut to the decking's slope
    if (friezeOn(f)) {
      const aW = Math.max(0, f.wallFt ?? 0)
      const zTopWall = f.zAt([f.origin[0] + d[0] * aW, f.origin[1] + d[1] * aW]) + topOffDv
      emitFrieze(f, fr, tailsAtStations, (p) => f.zAt(p) + topOffDv, zTopWall - friezeDpFt)
    }
    // fix round (Matt, 2026-08-13): the back span and the profiled exposed run
    // are ONE member — their joint at the wall line (every Wp lies on the
    // bearing line, one shared plane) draws no seam
    const dOriginTail = f.origin[0] * d[0] + f.origin[1] * d[1]

    for (const c of tailsAtStations) {
      const Wp: Pt2 = [f.origin[0] + e[0] * (c - originE), f.origin[1] + e[1] * (c - originE)]
      const tailEdges: number[] = []

      // section outline in (a, z): a = 0 at the inner end, wall line at 2·ov, tip
      // at L. The PROFILED prism spans the wall line → tip only (the eave wedge
      // widens outward at hips, so it stays on its face); the 2×-reach-in BACK
      // SPAN is emitted separately below, CLIPPED to the face polygon — a tail
      // near a hip is CUT BACK TO THE HIP RAFTER (Matt, 2026-08-11), never
      // allowed to run up through the neighbor face's sheathing (the tail's top
      // follows ITS face's plane, which sits above the neighbor's past the hip).
      const planAt = (a: number): Pt2 => [Wp[0] + d[0] * (a - 2 * ov), Wp[1] + d[1] * (a - 2 * ov)]
      const topAt = (a: number) => f.zAt(planAt(a)) + topOffDv
      // THE PROFILE RUN RULE (E4 r2, Matt): the decorative cut is INTRINSIC to the
      // member — it spans 2× the member's depth (capped by the exposure), never
      // stretched over the full overhang; full depth runs from the wall line to
      // the cut. Shared with the barge rafter, so a barge at the tail size shows
      // the exact same profile — lined up in elevation they read as one family.
      // r4: the run is AXIS-measured and the cut is MEMBER-FRAME
      // (memberEndProfile — square to the rafter, never sheared onto a vertical
      // grid); when the exposure cap bites, the rotated cut start can land a few
      // inches behind the wall line, buried at the wall (accepted).
      // ROOF BEARING WALLS (2026-10-05): the exposure starts at the wall's OUTSIDE face —
      // the overhang includes the wall standing past the bearing line, so the cut is
      // capped by what actually shows beyond it, never run back into the wall
      const Lp = Math.min(2 * tailDFt, Math.max(0.05, ov - (f.wallFt ?? 0)) * sq)
      const cutPts = memberEndProfile(profile.bottom, L, topAt(L), -f.m, 1, tailDFt, Lp)
      const outline: [number, number][] = []
      if (cutPts[0][0] > 2 * ov + 1e-9) outline.push([2 * ov, topAt(2 * ov) - depthDv])   // full-depth run to the cut
      outline.push(...cutPts)                                            // shaped bottom
      outline.push([L, topAt(L)])                                        // tip top (end face square to the axis)
      outline.push([2 * ov, topAt(2 * ov)])                              // wall-line top
      // (bottom left→right, tip up, back down the wall-line plumb = CCW in (a,z))

      // the back span: a plain full-depth box between the same planes, plan-clipped
      // to the face ring so it dies plumb at hips/valleys/the ridge
      {
        const half = tailWFt / 2
        const A0 = planAt(0), A1 = planAt(2 * ov)
        const innerRect: Pt2[] = [
          [A0[0] - e[0] * half, A0[1] - e[1] * half],
          [A0[0] + e[0] * half, A0[1] + e[1] * half],
          [A1[0] + e[0] * half, A1[1] + e[1] * half],
          [A1[0] - e[0] * half, A1[1] - e[1] * half],
        ]
        for (const r of keepRings(intersectClosed([innerRect], [f.ring]))) {
          // the back span sits buried in the mass (like the field rafters) — same
          // edges treatment so the whole tail reads outlined, not just its exposed tip
          prism(accTails, r, (p) => f.zAt(p) + topOffDv - depthDv, (p) => f.zAt(p) + topOffDv, tailEdges)
        }
      }

      // side caps + lateral strips + feature edges — the shared profiled emitter
      // (E4 extraction; byte-identical geometry to the old inline code)
      emitProfiledMember(accTails, tailEdges, planAt, d, e, tailWFt / 2, outline)
      // one uniform member: the back-span↔profiled joint at the wall line never
      // draws (Matt's styled-ends seam report, 2026-08-13)
      edgesTails.push(...stripSeamEdges(tailEdges, d, dOriginTail))
    }

    // FRAMING ENVELOPE: the whole tail (back span + profiled exposed run) swept along the
    // run of tails — a = 0 at the back span's inner end, the planAt frame above
    {
      const c0 = tailsAtStations[0]
      const W0: Pt2 = [f.origin[0] + e[0] * (c0 - originE), f.origin[1] + e[1] * (c0 - originE)]
      const planAt0 = (a: number): Pt2 => [W0[0] + d[0] * (a - 2 * ov), W0[1] + d[1] * (a - 2 * ov)]
      const topAt0 = (a: number) => f.zAt(planAt0(a)) + topOffDv
      const Lp = Math.min(2 * tailDFt, Math.max(0.05, ov - (f.wallFt ?? 0)) * sq)
      const cutPts = memberEndProfile(profile.bottom, L, topAt0(L), -f.m, 1, tailDFt, Lp)
      const whole: [number, number][] = [[0, topAt0(0) - depthDv]]
      if (cutPts[0][0] > 2 * ov + 1e-9) whole.push([2 * ov, topAt0(2 * ov) - depthDv])
      whole.push(...cutPts, [L, topAt0(L)], [0, topAt0(0)])
      const O = planAt0(0)
      const dO = O[0] * d[0] + O[1] * d[1]
      const cLo = Math.min(...tailsAtStations) - tailWFt / 2, cHi = Math.max(...tailsAtStations) + tailWFt / 2
      const at = (pe: number, a: number): Pt2 => [e[0] * pe + d[0] * (dO + a), e[1] * pe + d[1] * (dO + a)]
      for (const r of keepRings(intersectClosed([ccwRing([at(cLo, 0), at(cHi, 0), at(cHi, L), at(cLo, L)])], [f.ring]))) {
        envelope.push({ kind: 'profile', ring: r, origin: O, d: [d[0], d[1]], outline: whole })
      }
    }
  }

  // ---------- E4: rake outlookers + barge rafters (show members, like tails) ----------
  // E4 r2 (Matt, 2026-08-11, with screenshots + reference photo): the barge's
  // OUTER face aligns with the roof edge (the rake sheathing line) — it hangs
  // INBOARD of the overhang line, never past the intended overhang; its lower-end
  // cut follows THE PROFILE RUN RULE (2× depth, shared with the tails — see the
  // tails loop) so barge and tails read as one profile family in elevation; and
  // outlookers are DECORATIVE BRACKETS — heavy stock set BELOW the barge, level,
  // projecting from the rake wall — not the flat hidden-at-decking members of
  // modern practice (that model, with its 2× reach-in, was r1 and is superseded).
  // E4 r3 (Matt, same day): brackets run 4″ PAST the barge's outer face (their
  // shaped ends show beyond it); end styles come from the one 15-profile chart
  // family (tailProfile — Straight Cut default); and at a gable RIDGE the two
  // per-rake ridge-end brackets COMBINE into one bracket centered on the ridge.
  // E4 r4 (Matt, same day): profile cuts are MEMBER-FRAME (memberEndProfile —
  // square to the rafter, not sheared onto a vertical grid), and the bracket
  // array starts no lower than the bearing-corner station (the first bracket
  // centers on the eave wall's bearing line — never hanging off the wall end).
  if (anyBarge) {
    const sk = roofSkeletonFor(el)
    const lines = sk ? specLines(solved) : []
    // stale r1 keys ('2x4' flat etc.) fall back to the 4×6 bracket default;
    // E5 r2: settings read from the join authority like the tails' (src above)
    const outlooker = lumberSize(src.params.outlookerSize, OUTLOOKER_SIZES) ?? OUTLOOKER_SIZES[1]
    const outSpacingFt = Math.max(1, Number(src.params.outlookerSpacingIn) || 24) / 12
    // OUTLOOKER COUNT (2026-10-09, Matt: "I had to set the maximum spacing to 114 inches just to
    // display three outlookers"): 'count' lays EXACTLY this many brackets per rake, equally spaced
    // (the shared peak bracket on a paired gable is extra and keeps the same bay)
    const outCount = src.params.outlookerLayout === 'count'
      ? Math.max(1, Math.min(24, Math.round(Number(src.params.outlookerCount) || 3)))
      : 0
    const outProfile = tailProfile(src.params.outlookerStyle)
    // barge size: '' = match the roof's tail size (the show-member coherence rule)
    const barge = lumberSize(src.params.bargeSize, TAIL_SIZES) ?? tail
    const outWFt = outlooker.widthIn / 12
    const outDFt = outlooker.depthIn / 12
    const bargeWFt = barge.widthIn / 12
    const bargeDFt = barge.depthIn / 12

    // one toggled-rake context per skeleton rake edge, collected first so ridge
    // partners can be PAIRED before anything emits (r3's centered ridge bracket)
    interface RakeCtx {
      ov: number; f: RoofFace; sq: number; topOffDv: number; n: Pt2
      /** ROOF BEARING WALLS: the gable wall's reach past the bearing line (part of ov) */
      wallFt: number
      Ar: Pt2; Br: Pt2; Lr: number; uR: Pt2; mRake: number; rtR: number
      zLo: number; zHi: number; bargeDDv: number; bargeLpAxis: number
      cCorner: number
      /** E5 r5: the rake's EXPOSED stretches (plan distances along Ar→Br) —
       *  what remains after removing stretches covered by a joined partner's
       *  coplanar sheet. Barge segments and bracket stations live only here,
       *  so a PARTIALLY covered rake (Matt's telescoping peak standing above
       *  the joiner) keeps its members on the exposed run alone. */
      kept: [number, number][]
      paired: boolean
      /** TEAR r5: this rake was extended down-slope to its IDEAL extent (a
       *  flying rake continues past the kiss as if the lower roof weren't
       *  there) — members lay the full rhythm, then boolean against the lower
       *  sheets' tops. */
      extended: boolean
      /** TEAR r9: the stations this rake ACTUALLY OWNS — each constituent
       *  ctx's run, shifted through merges and the ideal extension. Members
       *  may only EXIST here: the rhythm spans the full ideal run, but a
       *  station on the extension (over the lower gable's own sheet, or its
       *  eave band) carries nothing — the barge there is inside or under
       *  another sheet, never an exposed rake member. */
      ownSpans: [number, number][]
    }
    const rakes: RakeCtx[] = []
    for (const edge of sk?.edges ?? []) {
      if (edge.kind !== 'rake') continue
      // map the rake back to its boundary spec geometrically (both endpoints on the
      // spec's roof-edge line — the gable edge solves no face, so only the line
      // mapping can carry its toggle; same matcher as trim.ts)
      const A2: Pt2 = [edge.a[0], edge.a[1]], B2: Pt2 = [edge.b[0], edge.b[1]]
      const line = lines.find((L) => distToLine(A2, L.o, L.d) <= 1e-3 && distToLine(B2, L.o, L.d) <= 1e-3)
      if (!line || !line.spec.barge) continue
      const ov = line.spec.overhangFt
      if (!(ov > 0.05)) continue                 // no rake overhang = nothing to cantilever
      const f = solved.faces[edge.faces[0]]
      if (!f) continue
      // orient the rake low (eave end) → high (ridge end)
      const lo3 = edge.a[2] <= edge.b[2] ? edge.a : edge.b
      const hi3 = edge.a[2] <= edge.b[2] ? edge.b : edge.a
      const Ar: Pt2 = [lo3[0], lo3[1]], Br: Pt2 = [hi3[0], hi3[1]]
      const Lr = Math.hypot(Br[0] - Ar[0], Br[1] - Ar[1])
      // short rakes are dropped AFTER the r4 merge below, not here — a flying
      // rake's apex stub can be a fraction of a foot and must chain first
      if (Lr < 1e-4) continue
      const sq = Math.sqrt(1 + f.m * f.m)
      // E5 r5: the rake's EXPOSED stretches — probe just outboard of the rake
      // line; stretches where a joined partner's sheet continues COPLANAR are
      // covered (its roof carries on there — nothing decorative underneath).
      // A fully buried rake keeps no interval and drops entirely; a PARTIALLY
      // covered one (the peak standing above the joiner) keeps its exposed run.
      const dvOwn = el.assemblyThicknessFt * sq
      const kept: [number, number][] = (join?.seamFaces?.length
        ? seamKeptIntervals(
            [Ar[0] + line.n[0] * 0.02, Ar[1] + line.n[1] * 0.02],
            [Br[0] + line.n[0] * 0.02, Br[1] + line.n[1] * 0.02],
            (p) => f.zAt(p) + dvOwn,
            join.seamFaces,
            'cover',
          )
        : [[0, 1]]
      ).map(([a, b]) => [a * Lr, b * Lr] as [number, number])
      if (!kept.length) continue
      const uR: Pt2 = [(Br[0] - Ar[0]) / Lr, (Br[1] - Ar[1]) / Lr]
      const mRake = (hi3[2] - lo3[2]) / Lr
      const rtR = Math.sqrt(1 + mRake * mRake)
      // r4 (Matt: "extending partially off the end of the wall corner"): brackets
      // exist only where the gable WALL stands behind them — find where the eave
      // wall's bearing line crosses the rake by projecting the gable spec's own
      // bearing endpoints onto the rake axis; the lower one is the corner station
      const sA = (line.spec.a[0] - Ar[0]) * uR[0] + (line.spec.a[1] - Ar[1]) * uR[1]
      const sB = (line.spec.b[0] - Ar[0]) * uR[0] + (line.spec.b[1] - Ar[1]) * uR[1]
      rakes.push({
        ov, f, sq, wallFt: line.spec.wallFt ?? 0,
        topOffDv: (zones.belowFt + zones.coreFt - deckFt) * sq,      // decking underside
        n: line.n,                                 // the gable edge's outward plan normal
        Ar, Br, Lr, uR,
        mRake,                                     // rise per plan ft along the rake
        rtR,                                       // the rake's own slope factor √(1+mRake²)
        zLo: lo3[2], zHi: hi3[2],
        bargeDDv: bargeDFt * rtR,                  // plumb drop of the barge's parallel bottom
        // THE PROFILE RUN RULE (r2, AXIS-measured since r4): intrinsic to the
        // member — 2× its depth along the axis, capped by half the rake;
        // identical to the tails' rule, so the default match-tail-size barge
        // shows the exact same cut
        bargeLpAxis: Math.min(2 * bargeDFt, (Lr * rtR) / 2),
        cCorner: Math.max(0, Math.min(sA, sB)),
        kept,
        paired: false,
        extended: false,
        ownSpans: [[0, Lr]],
      })
    }

    // TEAR r4 (2026-08-12, Matt's "scrunched outlookers + stacked barges"): a
    // FLYING RAKE shares its rail line with the fixed rake band, so the census
    // yields two (or three) collinear rake edges that are ONE physical rake —
    // per-segment barges butted mid-run and per-segment outlooker arrays with
    // restarted rhythms. MERGE chained collinear ctxs (one's high end = the
    // next's low end, in 3D, same slope) into a single run before pairing, so
    // the barge spans the true rake with real end cuts and the bracket array
    // lays one rhythm from the true corner. Opposite-slope rakes meeting at an
    // apex never chain (their shared point is hi↔hi) — they PAIR below instead.
    for (let changed = true; changed;) {
      changed = false
      outer: for (let a = 0; a < rakes.length; a++) {
        for (let b = 0; b < rakes.length; b++) {
          if (a === b) continue
          const X = rakes[a], Y = rakes[b]   // X's TOP meets Y's BOTTOM
          if (Math.hypot(X.Br[0] - Y.Ar[0], X.Br[1] - Y.Ar[1]) > 1e-4) continue
          if (Math.abs(X.zHi - Y.zLo) > 1e-4) continue
          if (Math.abs(X.mRake - Y.mRake) > 1e-6) continue
          if (X.uR[0] * Y.uR[0] + X.uR[1] * Y.uR[1] < 1 - 1e-9) continue
          const Lr = X.Lr + Y.Lr
          const mMerged = (Y.zHi - X.zLo) / Lr
          const rtMerged = Math.sqrt(1 + mMerged * mMerged)
          rakes[a] = {
            ...X,
            ownSpans: [...X.ownSpans, ...Y.ownSpans.map(([s, e]) => [s + X.Lr, e + X.Lr] as [number, number])]
              .sort((p, q) => p[0] - q[0])
              .reduce<[number, number][]>((acc, iv) => {
                const last = acc[acc.length - 1]
                if (last && iv[0] <= last[1] + 1e-6) last[1] = Math.max(last[1], iv[1])
                else acc.push([iv[0], iv[1]])
                return acc
              }, []),
            Br: Y.Br, Lr, zHi: Y.zHi,
            mRake: mMerged,
            rtR: rtMerged,
            bargeLpAxis: Math.min(2 * bargeDFt, (Lr * rtMerged) / 2),
            kept: [...X.kept, ...Y.kept.map(([s, e]) => [s + X.Lr, e + X.Lr] as [number, number])]
              .sort((p, q) => p[0] - q[0])
              .reduce<[number, number][]>((acc, iv) => {
                const last = acc[acc.length - 1]
                if (last && iv[0] <= last[1] + 1e-6) last[1] = Math.max(last[1], iv[1])
                else acc.push([iv[0], iv[1]])
                return acc
              }, []),
          }
          rakes.splice(b, 1)
          changed = true
          break outer
        }
      }
    }
    // now drop anything still too short to carry a member (pre-merge this ran
    // during collection, which silently ate flying-rake apex stubs and left the
    // merged run unpaired at the ridge)
    for (let i = rakes.length - 1; i >= 0; i--) {
      if (rakes[i].Lr < outWFt) rakes.splice(i, 1)
    }

    // TEAR r5 (Matt: "the barge should assume the full extent of the high
    // roof … boolean out anything that would pass the plane of the telescoping
    // roof"): a FLYING rake's members derive their run from the IDEAL rake —
    // extended down-slope to the eave ring as if the lower roof weren't there,
    // so both sides of the gable lay identical outlooker rhythms and the barge
    // spans the true gable — and the emission below then CLIPS the geometry
    // against the lower sheets' top surfaces. An ordinary rake's low end sits
    // ON the eave ring (the point just past it is outside), so it never
    // extends and stays byte-identical.
    for (const r of rakes) {
      const back: Pt2 = [r.Ar[0] - r.uR[0] * 0.02, r.Ar[1] - r.uR[1] * 0.02]
      if (!pointInRing(back, solved.eaveRing)) continue
      const u: Pt2 = [-r.uR[0], -r.uR[1]]
      let dExt = Infinity
      const ne = solved.eaveRing.length
      for (let i = 0; i < ne; i++) {
        const q0 = solved.eaveRing[i], q1 = solved.eaveRing[(i + 1) % ne]
        const ex2 = q1[0] - q0[0], ey2 = q1[1] - q0[1]
        const den = u[0] * ey2 - u[1] * ex2
        if (Math.abs(den) < 1e-12) continue
        const t = ((q0[0] - r.Ar[0]) * ey2 - (q0[1] - r.Ar[1]) * ex2) / den
        const s = ((q0[0] - r.Ar[0]) * u[1] - (q0[1] - r.Ar[1]) * u[0]) / den
        if (t > 1e-6 && s >= -1e-7 && s <= 1 + 1e-7 && t < dExt) dExt = t
      }
      if (!Number.isFinite(dExt) || dExt < 1e-4 || dExt > 1e4) continue
      r.Ar = [r.Ar[0] - r.uR[0] * dExt, r.Ar[1] - r.uR[1] * dExt]
      r.Lr += dExt
      r.zLo -= r.mRake * dExt
      r.bargeLpAxis = Math.min(2 * bargeDFt, (r.Lr * r.rtR) / 2)
      const shifted = r.kept.map(([a, b]) => [a + dExt, b + dExt] as [number, number])
      r.kept = shifted.length && shifted[0][0] <= dExt + 1e-6
        ? [[0, shifted[0][1]], ...shifted.slice(1)]
        : [[0, dExt], ...shifted]
      // the extension is NOT owned — members never emit there (r9)
      r.ownSpans = r.ownSpans.map(([s, e]) => [s + dExt, e + dExt] as [number, number])
      // the (future) gable-infill wall backs the flying stretch — the r4
      // wall-behind rule keys off the rail spec's span, which stops at the
      // tear; the surface boolean below is what culls truly buried members
      r.cCorner = 0
      r.extended = true
    }

    // pair rakes whose RIDGE ends meet (the two rakes of a gable peak) — their
    // per-rake ridge-end brackets are replaced by ONE bracket centered on the
    // ridge (r3, Matt: "combine into just one … instead of two side-by-side")
    const ridgeKey = (p: Pt2) => `${Math.round(p[0] * 1e4)},${Math.round(p[1] * 1e4)}`
    const byRidge = new Map<string, number[]>()
    rakes.forEach((r, i) => {
      const k = ridgeKey(r.Br)
      const arr = byRidge.get(k)
      if (arr) arr.push(i)
      else byRidge.set(k, [i])
    })
    const ridgePairs: [number, number][] = []
    for (const idxs of byRidge.values()) {
      if (idxs.length !== 2) continue
      rakes[idxs[0]].paired = true
      rakes[idxs[1]].paired = true
      ridgePairs.push([idxs[0], idxs[1]])
    }

    // one bracket: LEVEL member from the bearing line (buried in the rake wall,
    // which stands outboard of it) out to 4″ PAST the barge's outer face (r3 —
    // the shaped end shows beyond the barge), top T seated on the barge's
    // underside, outer end shaped by the end style over the intrinsic run.
    // r5: zCover clips the bracket flat where the lower roof's sheet rises
    // through it (a bracket straddling the surface sits cut on it).
    const clipAbove = (pts: [number, number][], zMin2: number): [number, number][] => {
      const out: [number, number][] = []
      for (let i = 0; i < pts.length; i++) {
        const cur = pts[i], prev = pts[(i + pts.length - 1) % pts.length]
        const fc = cur[1] - zMin2, fp = prev[1] - zMin2
        if (fc >= -1e-9) {
          if (fp < -1e-9) { const t = fp / (fp - fc); out.push([prev[0] + (cur[0] - prev[0]) * t, zMin2]) }
          out.push(cur)
        } else if (fp >= -1e-9) { const t = fp / (fp - fc); out.push([prev[0] + (cur[0] - prev[0]) * t, zMin2]) }
      }
      return out
    }
    const emitBracket = (r: RakeCtx, S: Pt2, T: number, zCover?: number) => {
      const ovEnd = r.ov + OUTLOOKER_PAST_BARGE_FT
      const planAt = (a: number): Pt2 => [S[0] + r.n[0] * (a - r.ov), S[1] + r.n[1] * (a - r.ov)]
      // ROOF BEARING WALLS: the shaped end stays outside the gable wall's exterior face
      const outLp = Math.min(2 * outDFt, Math.max(0.05, ovEnd - r.wallFt))
      // brackets are LEVEL members, so the member frame (r4) coincides with the
      // vertical grid — routed through the one helper anyway
      const cutPts = memberEndProfile(outProfile.bottom, ovEnd, T, 0, 1, outDFt, outLp)
      let outline: [number, number][] = []
      if (cutPts[0][0] > 1e-9) outline.push([0, T - outDFt])         // full-depth run to the cut
      outline.push(...cutPts)
      outline.push([ovEnd, T])                                       // outer-end top
      outline.push([0, T])                                           // wall-line top
      if (zCover !== undefined && zCover > T - outDFt + 1e-6) {
        outline = clipAbove(outline, zCover)
        if (outline.length < 3) return
      }
      emitProfiledMember(accOut, edgesOut, planAt, r.n, r.uR, outWFt / 2, outline)
    }

    for (const r of rakes) {
      // ---- barge rafter first (the brackets seat against its underside): OUTER
      // face ON the overhang line — the rake sheathing edge — hanging inboard by
      // its width; top at the decking underside so the decking runs over it.
      // Lower end cut with the roof's own tailStyle over the SHARED intrinsic
      // profile run; upper end a plumb cut at the ridge station — the partner
      // barge from the other face lands on the same collinear line and the two
      // plumb faces meet watertight (the classic peak joint).
      const bargeCenterAt = (a: number): Pt2 => [
        r.Ar[0] + r.uR[0] * a - r.n[0] * (bargeWFt / 2),
        r.Ar[1] + r.uR[1] * a - r.n[1] * (bargeWFt / 2),
      ]
      // z from the rake edge's own endpoints (linear along the edge, level across
      // the width — a barge's top edge is level across its thickness)
      const bTopAt = (a: number) => r.zLo + r.mRake * a + r.topOffDv
      // r5: the LOWER sheets' cover intervals along the barge center line —
      // subtracted from every member of an EXTENDED (flying) rake. Each
      // interval's top surface is linear along the line, so the boolean's cut
      // edge IS the roof-surface line.
      const dvOf = (g: RoofFace) => el.assemblyThicknessFt * Math.sqrt(1 + g.m * g.m)
      const covers: { g: RoofFace; a0: number; a1: number }[] = []
      if (r.extended) {
        const C0 = bargeCenterAt(0), C1 = bargeCenterAt(r.Lr)
        const cx = C1[0] - C0[0], cy = C1[1] - C0[1]
        for (const g of solved.faces) {
          if (g.flying || g === r.f) continue
          // r7 (the barge-apex gap): a face COPLANAR with this rake's own plane
          // is the rake's own sheet, not a lower roof — the parent's band piece
          // crosses the barge line whenever the band corner sits below the
          // ridge (a small eave overhang on the lower roof), and treating it
          // as a cover boolean'd the barge away under its own sheet
          if (Math.abs(g.m - r.f.m) <= 1e-9 &&
              g.uphill[0] * r.f.uphill[0] + g.uphill[1] * r.f.uphill[1] >= 1 - 1e-9 &&
              Math.abs(g.zAt(C0) - r.f.zAt(C0)) <= 1e-9) continue
          const ts: number[] = [0, 1]
          for (let i = 0; i < g.ring.length; i++) {
            const q0 = g.ring[i], q1 = g.ring[(i + 1) % g.ring.length]
            const ex2 = q1[0] - q0[0], ey2 = q1[1] - q0[1]
            const den = cx * ey2 - cy * ex2
            if (Math.abs(den) < 1e-12) continue
            const t = ((q0[0] - C0[0]) * ey2 - (q0[1] - C0[1]) * ex2) / den
            const s = ((q0[0] - C0[0]) * cy - (q0[1] - C0[1]) * cx) / den
            if (t > 1e-9 && t < 1 - 1e-9 && s >= -1e-9 && s <= 1 + 1e-9) ts.push(t)
          }
          ts.sort((p, q) => p - q)
          for (let i = 0; i + 1 < ts.length; i++) {
            if (ts[i + 1] - ts[i] < 1e-6) continue
            const m = (ts[i] + ts[i + 1]) / 2
            if (!pointInRing([C0[0] + cx * m, C0[1] + cy * m], g.ring)) continue
            covers.push({ g, a0: ts[i] * r.Lr, a1: ts[i + 1] * r.Lr })
          }
        }
      }
      const coverAt = (c: number) => covers.find((cv) => c >= cv.a0 - 1e-9 && c <= cv.a1 + 1e-9)
      // r9 (Matt's "the rafter tails aren't spacing equally" — the ideal
      // stretch's barge tip + low bracket emitting down at the eave among the
      // tails): members exist only on the rake's OWN spans, never the
      // extension (which runs over the lower gable's sheet and eave band)
      const inOwn = (c: number) => r.ownSpans.some(([a0, a1]) => c >= a0 - 1e-6 && c <= a1 + 1e-6)
      // E5 r5: one barge SEGMENT per exposed stretch. The true eave end keeps
      // the tail-style MEMBER-FRAME cut (r4 — profile order runs cut start →
      // tip, reversed for the ascending-a bottom walk, closing square to the
      // axis); a segment starting mid-rake (its lower stretch buried under the
      // joined partner) gets a plumb cut at the seam, like the ridge end.
      for (const [a0, a1] of r.kept) {
        if (a1 - a0 < 0.2) continue
        const bOutline: [number, number][] = []
        if (a0 <= 1e-6) {
          const cutPts = memberEndProfile(profile.bottom, 0, bTopAt(0), r.mRake, -1, bargeDFt, r.bargeLpAxis)
          bOutline.push(...[...cutPts].reverse())
        } else {
          bOutline.push([a0, bTopAt(a0) - r.bargeDDv])               // plumb cut at the seam
        }
        bOutline.push([a1, bTopAt(a1) - r.bargeDDv])                 // upper-end bottom
        bOutline.push([a1, bTopAt(a1)])                              // upper-end top (plumb cut)
        bOutline.push([a0 <= 1e-6 ? 0 : a0, bTopAt(a0 <= 1e-6 ? 0 : a0)])   // lower-end top
        if ((covers.length || r.extended) && clipperReady()) {
          // r5: die on the lower roof — subtract each cover's below-surface
          // region (a trapezoid up to its top line in (a,z) space); the barge's
          // visible end becomes the roof-surface cut, buried stretches vanish
          const clips: Pt2[][] = covers.map((cv) => [
            [cv.a0, -1e4], [cv.a1, -1e4],
            [cv.a1, cv.g.zAt(bargeCenterAt(cv.a1)) + dvOf(cv.g)],
            [cv.a0, cv.g.zAt(bargeCenterAt(cv.a0)) + dvOf(cv.g)],
          ] as Pt2[])
          // r9: nothing exists outside the rake's own spans — full-height cuts
          // over the gaps (the ideal extension over the lower gable's sheet)
          if (r.extended) {
            let cur = -1
            for (const [i0, i1] of [...r.ownSpans, [r.Lr + 1, r.Lr + 1] as [number, number]]) {
              if (i0 - cur > 1e-4) clips.push([[cur, -1e4], [i0, -1e4], [i0, 1e4], [cur, 1e4]] as Pt2[])
              cur = Math.max(cur, i1)
            }
          }
          const srcOrient = signedArea(bOutline as unknown as Pt2[]) > 0
          let pieces: Pt2[][] = []
          try { pieces = differenceClosed([bOutline as unknown as Pt2[]], clips) } catch { pieces = [bOutline as unknown as Pt2[]] }
          for (const piece of pieces) {
            if (piece.length < 3 || Math.abs(signedArea(piece)) < 1e-4) continue
            const outl = (signedArea(piece) > 0) === srcOrient ? piece : [...piece].reverse()
            emitProfiledMember(accBarge, edgesBarge, (a) => bargeCenterAt(a), r.uR, r.n, bargeWFt / 2, outl as [number, number][])
          }
        } else {
          emitProfiledMember(accBarge, edgesBarge, (a) => bargeCenterAt(a), r.uR, r.n, bargeWFt / 2, bOutline)
        }
      }

      // ---- brackets: stations distribute along the rake (tailStations — a
      // member at each end, spacing is a MAX), starting clear of the barge's
      // shaped tip so no bracket hangs under the cut AND no lower than the
      // bearing-corner station (r4, Matt: the low bracket was hanging off the
      // gable wall's end over the eave band — the first bracket now centers on
      // the eave wall's own bearing line, so there is always wall behind it).
      // Tops seat on the barge's underside sampled at the bracket's up-rake
      // edge (a hair of hidden embed into the sloped bottom, never a visible
      // wedge gap). A PAIRED rake lays out to the ridge itself and drops that
      // last station — the shared centered bracket below replaces it, keeping
      // the equal-spacing rhythm.
      const loStation = Math.max(r.bargeLpAxis / r.rtR + outWFt / 2, r.cCorner)
      const hiStation = r.paired ? r.Lr : r.Lr - outWFt / 2
      for (const c of outCount ? countStations(loStation, hiStation, outCount, r.paired) : tailStations(loStation, hiStation, outSpacingFt)) {
        if (r.paired && c >= r.Lr - 1e-6) continue
        const S: Pt2 = [r.Ar[0] + r.uR[0] * c, r.Ar[1] + r.uR[1] * c]   // station on the rake line
        const cv = coverAt(c)
        // a bracket whose outer run leaves this face's plan region is skipped
        // (the tails tip-probe guard, sampled just inside the rake line) —
        // unless the LOWER roof continues there (r5: the extended stretch lies
        // past the flying strip's own ring; the surface boolean governs it)
        const probe: Pt2 = [S[0] - r.n[0] * r.ov * 0.02, S[1] - r.n[1] * r.ov * 0.02]
        if (!pointInRing(probe, r.f.ring) && !cv) continue
        // E5 r5: only stations on the rake's EXPOSED stretches carry brackets
        if (!r.kept.some(([a0, a1]) => c >= a0 - 1e-6 && c <= a1 + 1e-6)) continue
        const T = r.zLo + r.mRake * Math.min(c + outWFt / 2, r.Lr) + r.topOffDv - r.bargeDDv
        // r9: a bracket exists only on the rake's own spans (its barge is
        // what it seats under — no barge stretch, no bracket)
        if (r.extended && !inOwn(c)) continue
        // r5: a bracket fully below the lower sheet's top vanishes; a
        // straddler gets clipped flat on the surface inside emitBracket
        const zCover = cv ? cv.g.zAt(S) + dvOf(cv.g) : undefined
        if (zCover !== undefined && T <= zCover + 1e-6) continue
        emitBracket(r, S, T, zCover)
      }
    }

    // ---- the centered RIDGE bracket (r3): one member straddling the peak,
    // seated under BOTH barges (top from the lower of the two undersides at the
    // bracket's edges — symmetric gables embed a hair into each barge toward
    // the ridge center, asymmetric ones stay gap-free on the shallower side)
    for (const [ia, ib] of ridgePairs) {
      const ra = rakes[ia], rb = rakes[ib]
      // E5 r5: the peak bracket exists only when BOTH rakes' ridge ends are
      // exposed (a covered peak carries nothing decorative)
      const atRidge = (r: RakeCtx) => r.kept.some(([a0, a1]) => r.Lr >= a0 - 1e-6 && r.Lr <= a1 + 1e-6)
      if (!atRidge(ra) || !atRidge(rb)) continue
      // E5 r9 (Matt): the apex bracket seats with its TOP CENTER touching the
      // BOTTOM of the barge junction — the two barge undersides then descend
      // across it toward its edges, each notching a birds-mouth seat over the
      // bracket, exactly the read the slope outlookers get. (No edge-sample
      // drop here — that left the junction's V floating half-a-width of slope
      // above the bracket. min across an asymmetric pair keeps the shallower
      // side gap-free; a symmetric peak touches exactly.)
      const T = Math.min(
        ra.zHi + ra.topOffDv - ra.bargeDDv,
        rb.zHi + rb.topOffDv - rb.bargeDDv,
      )
      emitBracket(ra, ra.Br, T)
    }
  }

  // D1: build up to two solids — study (framing: true, 2D-excluded) first, then
  // show members. Same groupKey on both: the pipeline merges them into the one
  // 3D chunk the old single solid produced.
  const solidOf = (a: MeshAcc, e: number[], study: boolean, kind?: 'tails' | 'outlookers' | 'barge' | 'frieze'): ElementSolid | null => {
    if (a.vc === 0) return null
    let zMin = Infinity, zMax = -Infinity
    for (let i = 2; i < a.pos.length; i += 3) {
      if (a.pos[i] < zMin) zMin = a.pos[i]
      if (a.pos[i] > zMax) zMax = a.pos[i]
    }
    return {
      elementId: el.id,
      category: 'roof',
      outlines: [],
      zBase: zMin,
      zTop: zMax,
      // FRIEZE BOARD: trim in the fascia's finish, keyed `|fascia|` so the Paint Brush
      // paints it with the fascia (not a framing chunk)
      materialId: kind === 'frieze' && friezeFinish ? friezeFinish.materialId : framingMaterialId(el),
      // a member kind keys its own chunk (the Paint Bucket reads it off the hit);
      // still `…|framing`, so the Systems display classes it with the rest
      groupKey: `roof|${el.levelId}|${el.type.assemblyId ?? 'none'}|${el.id}${kind === 'frieze' ? '|fascia|frieze' : `${kind ? `|${kind}` : ''}|framing`}`,
      // COLORS: the brushed / standard color on this member kind (3D only)
      ...(kind === 'frieze'
        ? (friezeFinish?.surfaceMaterialId ? { surfaceMaterialId: friezeFinish.surfaceMaterialId } : {})
        : kind && el.roofMemberColors?.[kind]
          ? { surfaceMaterialId: tintMaterialId(el.roofMemberColors[kind]!, framingMaterialId(el)) } : {}),
      pieces: [],
      placements: [],
      openingInstances: [],
      mesh: {
        positions: new Float32Array(a.pos),
        normals: new Float32Array(a.nrm),
        indices: new Uint32Array(a.idx),
        edges: e,
        uvs: meshAccUvs(a),
      },
      meshOnly: true,
      skipProjectedOutline: true,
      ...(study ? { framing: true } : kind !== 'frieze' ? { lumber: true } : {}),
      // FRAMING ENVELOPE rides the study solid — the one whose presence marks the roof
      // framed (hollow body) for the section's assembly-depth tone
      ...(study && envelope.length ? { roofEnvelope: envelope } : {}),
    }
  }
  const out = [
    solidOf(accStudy, edgesStudy, true), solidOf(accShow, edgesShow, false),
    solidOf(accTails, edgesTails, false, 'tails'), solidOf(accOut, edgesOut, false, 'outlookers'),
    solidOf(accBarge, edgesBarge, false, 'barge'),
    solidOf(accFrieze, edgesFrieze, false, 'frieze'),
  ]
    .filter((s): s is ElementSolid => !!s)
  return out.length ? out : null
}
